/**
 * API Client for Supabase Backend (replaces GAS Backend)
 */
import { supabase } from '@/lib/supabase';
import { Client, CheckIn, InventoryItem, GymClass, ClassBooking, Webhook, Gym, User, DashboardStats, Status, ClassAttendee, Membership, MembershipPlan, Expense, Sale, SaleItem, SupportTicket, SupportMessage, CashBox, CashMovement, PaymentTransaction, NutritionPlan, NutritionMeal, NutritionProgress,
  NutritionClinicalHistory, NutritionAppointment, NutritionAvailability, NutritionAvailabilityBlock,
  NutritionBookingConfig, Reward, RewardPrize, RewardParticipation, ClientReward,
  RaffleEntry, RewardLeaderboard, RewardProgressEntry, RewardAccessCode, RewardCodeVerification } from '@/types';
import { sanitizePhone } from './phone-utils';
import { compressImage, extensionForMime } from './image-utils';
import { dateKeyInTimeZone, todayKeyInTimeZone, addDaysToDateKey, dayRangeToUtc, getBrowserTimeZone } from './date-tz';

const PLACEHOLDER_LAST = '.';
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Detecta fallos de red de supabase-js: cuando el fetch no obtiene respuesta,
 * postgrest-js devuelve `{ message, details: <stack>, hint: '', code: '' }`.
 * Un error real de Postgres/PostgREST siempre trae un `code` no vacío.
 */
export const isNetworkRpcError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  if (error instanceof TypeError) return true;
  return (error as any).code === '';
};

const cleanName = (first: string | null | undefined, last: string | null | undefined): string => {
  const f = (first || '').trim();
  const l = (last || '').trim();
  if (!l || l === PLACEHOLDER_LAST) return f;
  return `${f} ${l}`.trim();
};

// Data Mappers (Postgres -> App Types)
const mapClient = (row: any): Client => ({
  client_id: row.id,
  gym_id: row.gym_id,
  name: cleanName(row.first_name, row.last_name),
  email: row.email || '',
  phone: row.phone || '',
  qr_code: row.qr_code || '',
  status: row.status as Status,
  created_at: row.created_at,
  photo_path: row.photo_path || null,
  emergency_contact: row.emergency_contact,
  notes: row.notes,
  age: row.age,
  sex: row.sex,
  current_sessions: row.current_sessions_remaining,
  current_end_date: row.current_end_date
});

const mapCheckIn = (row: any): CheckIn => ({
  checkin_id: row.id,
  client_id: row.client_id,
  gym_id: row.gym_id,
  timestamp: row.created_at,
  result: row.result as 'allowed' | 'denied',
  client_name: row.client_name_snapshot || (row.clients ? cleanName(row.clients.first_name, row.clients.last_name) : 'Desconocido'),
  reason: row.message,
  method: row.method,
  sessions_remaining: row.sessions_remaining
});

const mapInventory = (row: any): InventoryItem => {
  let status = row.status;
  // If status is not one of the expected ones, derive it from quantity
  if (!['in_stock', 'low_stock', 'out_of_stock'].includes(status)) {
    if ((row.quantity || 0) <= 0) status = 'out_of_stock';
    else if ((row.quantity || 0) <= (row.min_stock || 0)) status = 'low_stock';
    else status = 'in_stock';
  }

  return {
    item_id: row.id,
    gym_id: row.gym_id,
    name: row.item_name,
    category: row.category,
    quantity: row.quantity,
    min_stock: row.min_stock,
    cost_per_unit: row.cost_per_unit,
    selling_price: row.selling_price || 0,
    unit: row.unit || 'units',
    supplier: row.supplier,
    status: status as any,
    notes: row.notes,
    created_at: row.created_at
  };
};

const mapSale = (row: any): Sale => ({
  id: row.id,
  gym_id: row.gym_id,
  client_id: row.client_id,
  cash_box_id: row.cash_box_id,
  total_amount: Number(row.total_amount),
  payment_method: row.payment_method,
  created_at: row.created_at,
  client_name: row.clients ? cleanName(row.clients.first_name, row.clients.last_name) : undefined,
  items: row.sale_items?.map(mapSaleItem)
});

const mapSaleItem = (row: any): SaleItem => ({
  id: row.id,
  sale_id: row.sale_id,
  product_id: row.product_id,
  quantity: row.quantity,
  unit_price: Number(row.unit_price),
  product_name: row.inventory?.item_name
});

const mapMembership = (row: any): Membership => ({
  membership_id: row.id,
  client_id: row.client_id,
  gym_id: row.gym_id,
  type: row.type,
  start_date: row.start_date,
  end_date: row.end_date,
  status: row.status as Status,
  price: Number(row.price),
  payment_status: row.payment_status || 'pending',
  payment_method: row.payment_method || 'cash',
  sessions_remaining: row.sessions_remaining,
  paid_at: row.paid_at,
  created_at: row.created_at
});

const mapClass = (row: any): GymClass => {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const spanishDays = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

  let dayIndex = 1;
  if (!isNaN(Number(row.day_of_week))) {
    dayIndex = Number(row.day_of_week);
  } else {
    const englishIdx = days.indexOf(row.day_of_week);
    const spanishIdx = spanishDays.indexOf(row.day_of_week);
    dayIndex = englishIdx !== -1 ? englishIdx : (spanishIdx !== -1 ? spanishIdx : 1);
  }

  return {
    class_id: row.id,
    gym_id: row.gym_id,
    name: row.name,
    description: row.description || '',
    instructor: row.instructor || '',
    day_of_week: dayIndex,
    start_time: row.start_time || '00:00',
    end_time: row.end_time || '00:00',
    capacity: row.capacity || 20,
    enrolled: row.class_bookings ? (Array.isArray(row.class_bookings) ? row.class_bookings.length : (row.class_bookings.count || 0)) : 0,
    room: row.room || '',
    status: row.status || 'active',
    created_at: row.created_at
  };
};

const mapPlan = (row: any): MembershipPlan => ({
  id: row.id,
  gym_id: row.gym_id,
  name: row.name,
  price: Number(row.price),
  description: row.description,
  duration_days: row.duration_days,
  total_sessions: row.total_sessions,
  included_modules: row.included_modules || [],
  is_private: row.is_private ?? false,
  created_at: row.created_at
});

const mapGym = (row: any): Gym => ({
  ...row,
  gym_id: row.id,
  plan: row.tier?.name || row.plan || 'Free',
  modules_enabled: row.modules_enabled || [],
  price: row.tier?.price || row.price || 0,
  is_trial: row.is_trial ?? false,
  status: row.status || 'active',
  expiry_date: row.expiry_date || '',
  created_at: row.created_at || new Date().toISOString(),
  slug: row.slug || '',
  setup_fee_paid: row.setup_fee_paid ?? false,
  setup_fee_amount: row.setup_fee_amount ?? 79.00,
  setup_completed_at: row.setup_completed_at || undefined,
  setup_completed_by: row.setup_completed_by || undefined,
});

const mapRewardPrize = (row: any): RewardPrize => ({
  id: row.id,
  reward_id: row.reward_id,
  gym_id: row.gym_id,
  label: row.label,
  weight: Number(row.weight ?? 1),
  stock: row.stock,
  remaining: row.remaining,
  sort_order: row.sort_order ?? 0,
  is_active: row.is_active ?? true,
  created_at: row.created_at,
});

const mapReward = (row: any): Reward => ({
  id: row.id,
  gym_id: row.gym_id,
  type: row.type,
  title: row.title,
  description: row.description,
  image_url: row.image_url,
  is_active: row.is_active ?? true,
  starts_at: row.starts_at,
  ends_at: row.ends_at,
  plays_per_client: row.plays_per_client ?? 1,
  config: row.config || {},
  created_by: row.created_by,
  created_at: row.created_at,
  updated_at: row.updated_at,
  prizes: row.reward_prizes
    ? row.reward_prizes.map(mapRewardPrize).sort((a: RewardPrize, b: RewardPrize) => a.sort_order - b.sort_order)
    : [],
  entries: row.reward_raffle_entries ? row.reward_raffle_entries.map(mapRaffleEntry) : [],
});

const mapRewardParticipation = (row: any): RewardParticipation => ({
  id: row.id,
  gym_id: row.gym_id,
  reward_id: row.reward_id,
  client_id: row.client_id,
  prize_id: row.prize_id,
  source: row.source,
  created_at: row.created_at,
  reward_title: row.rewards?.title,
  prize_label: row.reward_prizes?.label,
  client_name: row.clients ? cleanName(row.clients.first_name, row.clients.last_name) : undefined,
});

const mapRaffleEntry = (row: any): RaffleEntry => ({
  id: row.id,
  gym_id: row.gym_id,
  reward_id: row.reward_id,
  client_id: row.client_id,
  is_winner: row.is_winner ?? false,
  claimed_at: row.claimed_at,
  created_at: row.created_at,
  client_name: row.clients ? cleanName(row.clients.first_name, row.clients.last_name) : undefined,
});

const mapRewardProgress = (row: any): RewardProgressEntry => ({
  client_id: row.client_id,
  client_name: row.clients ? cleanName(row.clients.first_name, row.clients.last_name) : undefined,
  counted_days: Number(row.counted_days ?? 0),
  manual_adjustment: Number(row.manual_adjustment ?? 0),
  manual_adjustment_at: row.manual_adjustment_at ?? null,
  completed_at: row.completed_at,
  updated_at: row.updated_at,
});

const mapClientReward = (row: any): ClientReward => ({
  reward_id: row.reward_id,
  type: row.type,
  title: row.title,
  description: row.description,
  image_url: row.image_url,
  plays_per_client: row.plays_per_client ?? 1,
  prizes: (row.prizes || []).map((p: any) => ({
    id: p.id,
    label: p.label,
    weight: Number(p.weight ?? 1),
    sort_order: p.sort_order ?? 0,
  })),
  progress_count: row.progress_count,
  progress_target: row.progress_target,
  progress_window_days: row.progress_window_days,
  completed: row.completed,
  entered: row.entered,
  won: row.won,
  claimed: row.claimed,
  played: row.played,
  remaining_plays: row.remaining_plays,
});

// --- ERROR HANDLING HELPER ---
const handleApiError = (error: any) => {
  console.error('[API Error]:', error);
  
  // Wrap in a standard error but preserve Supabase properties for Sentry (via main.tsx beforeSend)
  let message = error.message || 'Ocurrió un error inesperado';
  
  if (error.code === 'P0001' || error.message?.includes('Rate limit')) {
    message = 'Demasiadas solicitudes. Por favor, intenta de nuevo en unos minutos.';
  } else if (error.code === '23505') {
    message = 'Este registro ya existe (duplicado).';
  } else if (error.code === '42P01') {
    message = 'Error de configuración: Tabla no encontrada.';
  }

  // Create a proper Error object but attach the Supabase properties
  const enhancedError = new Error(message);
  (enhancedError as any).code = error.code;
  (enhancedError as any).details = error.details;
  (enhancedError as any).hint = error.hint;
  (enhancedError as any).originalError = error;

  throw enhancedError;
};

// Thrown by getGymBySlug when the slug does not resolve to an existing gym,
// so callers can tell a genuinely missing gym apart from network failures.
export class GymNotFoundError extends Error {
  constructor(slug: string) {
    super(`Gym not found: ${slug}`);
    this.name = 'GymNotFoundError';
  }
}

// --- API IMPLEMENTATION ---

export const authApi = {
  // Legacy stubs - AuthContext uses supabase directly now
  login: async (creds: any) => ({ success: false, error: 'Use Supabase Auth' }),
  getMe: async (token: string) => ({ success: false, error: 'Use Supabase Auth' }),

  acceptTerms: async (userId: string, gymId: string | null, termsVersion: string, acceptanceMethod: string) => {
    let userIp = '0.0.0.0';
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      userIp = data.ip;
    } catch (e) {
      console.warn('Failed to fetch IP for terms acceptance:', e);
    }

    // 1. Insert into Audit Log
    const { error: logError } = await supabase
      .from('terms_acceptance_logs')
      .insert({
        user_id: userId,
        gym_id: gymId,
        terms_version: termsVersion,
        ip_address: userIp,
        acceptance_method: acceptanceMethod,
        user_agent: window.navigator.userAgent,
        accepted_at: new Date().toISOString()
      });

    if (logError) {
      console.error('Failed to insert terms acceptance log:', logError);
      // We continue anyway to update the profile so the user isn't blocked, 
      // but the log failure is serious.
    }

    // 2. Update Profile for state management
    const { error } = await supabase
      .from('profiles')
      .update({
        terms_accepted: true,
        terms_accepted_at: new Date().toISOString(),
        terms_accepted_ip: userIp
      })
      .eq('id', userId);

    if (error) throw error;
    return { success: true };
  }
};

export const gymApi = {
  getDashboardStats: async (_token: string | null, gymId?: string, startDate?: string, endDate?: string, timeZone?: string) => {
    const now = new Date();
    const isFiltered = !!(startDate && endDate);
    const tz = timeZone || getBrowserTimeZone();

    // Attendance day boundaries in the gym timezone
    const todayKey = todayKeyInTimeZone(tz);
    const yesterdayKey = addDaysToDateKey(todayKey, -1);
    const tomorrowKey = addDaysToDateKey(todayKey, 1);
    const todayStartISO = dayRangeToUtc(todayKey, todayKey, tz).startISO;
    const tomorrowStartISO = dayRangeToUtc(tomorrowKey, tomorrowKey, tz).startISO;
    const yesterdayStartISO = dayRangeToUtc(yesterdayKey, yesterdayKey, tz).startISO;

    // Date ranges — use custom dates if provided, otherwise current month
    const filterStart = startDate ? new Date(startDate).toISOString() : new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const filterEnd = endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString() : null;
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();

    // Attendance ranges (timezone aware)
    const attendanceRange = isFiltered ? dayRangeToUtc(startDate!, endDate!, tz) : null;
    const attendanceFilterStartISO = attendanceRange?.startISO ?? null;
    const attendanceFilterEndISO = attendanceRange?.endISO ?? null;
    const weeklyStartISO = isFiltered
      ? attendanceFilterStartISO!
      : dayRangeToUtc(addDaysToDateKey(todayKey, -6), todayKey, tz).startISO;
    const weeklyEndISO = isFiltered ? attendanceFilterEndISO! : tomorrowStartISO;

    // Queries
    let clientQuery = supabase.from('clients').select('id, created_at');
    let activeQuery = supabase.from('memberships').select('client_id').eq('status', 'active');

    let checkinsRangeQuery;
    let checkinsTodayQuery;
    let checkinsYesterdayQuery;
    let checkinsLastMonthQuery;
    let totalCheckinsQuery = supabase.from('check_ins').select('id', { count: 'exact', head: true });

    if (isFiltered) {
      checkinsRangeQuery = supabase.from('check_ins').select('created_at').gte('created_at', attendanceFilterStartISO!).lte('created_at', attendanceFilterEndISO!);
    } else {
      checkinsTodayQuery = supabase.from('check_ins').select('id', { count: 'exact', head: true }).gte('created_at', todayStartISO).lt('created_at', tomorrowStartISO);
      checkinsYesterdayQuery = supabase.from('check_ins').select('id', { count: 'exact', head: true }).gte('created_at', yesterdayStartISO).lt('created_at', todayStartISO);
      checkinsLastMonthQuery = supabase.from('check_ins').select('id', { count: 'exact', head: true }).gte('created_at', startOfLastMonth).lt('created_at', startOfCurrentMonth);
    }

    let weeklyCheckinsQuery = supabase.from('check_ins').select('created_at').gte('created_at', weeklyStartISO).lt('created_at', weeklyEndISO);

    let revenueQuery = supabase.from('memberships').select('price, start_date').eq('payment_status', 'paid');
    revenueQuery = isFiltered
      ? revenueQuery.gte('start_date', filterStart).lte('start_date', filterEnd!)
      : revenueQuery.gte('start_date', startOfLastMonth);

    let salesQuery = supabase.from('sales').select('total_amount, created_at');
    salesQuery = isFiltered
      ? salesQuery.gte('created_at', filterStart).lte('created_at', filterEnd!)
      : salesQuery.gte('created_at', startOfLastMonth);

    let inventoryQuery = supabase.from('inventory').select('quantity, cost_per_unit, created_at');
    inventoryQuery = isFiltered
      ? inventoryQuery.gte('created_at', filterStart).lte('created_at', filterEnd!)
      : inventoryQuery.gte('created_at', startOfLastMonth);

    let expensesQuery;
    const baseExpensesQuery = supabase.from('expenses').select('amount, date');
    if (isFiltered) {
      expensesQuery = baseExpensesQuery.gte('date', filterStart).lte('date', filterEnd!);
    } else {
      expensesQuery = baseExpensesQuery.gte('date', startOfLastMonth);
    }

    if (gymId) {
      clientQuery = clientQuery.eq('gym_id', gymId);
      activeQuery = activeQuery.eq('gym_id', gymId);
      totalCheckinsQuery = totalCheckinsQuery.eq('gym_id', gymId);

      if (isFiltered) {
        checkinsRangeQuery = checkinsRangeQuery!.eq('gym_id', gymId);
      } else {
        checkinsTodayQuery = checkinsTodayQuery!.eq('gym_id', gymId);
        checkinsYesterdayQuery = checkinsYesterdayQuery!.eq('gym_id', gymId);
        checkinsLastMonthQuery = checkinsLastMonthQuery!.eq('gym_id', gymId);
      }

      weeklyCheckinsQuery = weeklyCheckinsQuery.eq('gym_id', gymId);
      revenueQuery = revenueQuery.eq('gym_id', gymId);
      inventoryQuery = inventoryQuery.eq('gym_id', gymId);
      expensesQuery = expensesQuery.eq('gym_id', gymId);
      salesQuery = salesQuery.eq('gym_id', gymId);
    }

    const queries = isFiltered
      ? [clientQuery, activeQuery, checkinsRangeQuery!, weeklyCheckinsQuery, revenueQuery, inventoryQuery, expensesQuery, salesQuery, totalCheckinsQuery]
      : [clientQuery, activeQuery, checkinsTodayQuery!, checkinsYesterdayQuery!, checkinsLastMonthQuery!, weeklyCheckinsQuery, revenueQuery, inventoryQuery, expensesQuery, salesQuery, totalCheckinsQuery];

    const results = await Promise.all(queries);

    let clients: any[], activeMembers: any[], checkinsToday: number, checkinsYesterday: number, checkinsLastMonth: number,
        weeklyData: any[], revenueData: any[], inventoryData: any[], expensesData: any[], salesData: any[], totalCheckIns: number;

    if (isFiltered) {
      [clients, activeMembers, weeklyData, , revenueData, inventoryData, expensesData, salesData] = results.map((r: any) => r.data);
      checkinsToday = results[2]?.data?.length || 0;
      checkinsYesterday = 0;
      checkinsLastMonth = 0;
      totalCheckIns = results[8]?.count || 0;
    } else {
      ([{ data: clients }, { data: activeMembers }, { count: checkinsToday }, { count: checkinsYesterday },
        { count: checkinsLastMonth }, { data: weeklyData }, { data: revenueData }, { data: inventoryData },
        { data: expensesData }, { data: salesData }, { count: totalCheckIns }] = results as any);
    }

    // Calculate Stats
    const totalClients = clients?.length || 0;

    let clientsThisMonth: number, clientsLastMonth: number;
    let currentMonthMembershipRevenue: number, lastMonthMembershipRevenue: number;
    let currentMonthSalesRevenue: number, lastMonthSalesRevenue: number;
    let currentMonthExpenses: number, currentMonthGeneralExpenses: number;

    if (isFiltered) {
      clientsThisMonth = clients?.filter(c => c.created_at >= filterStart && c.created_at <= filterEnd!).length || 0;
      clientsLastMonth = 0;

      currentMonthMembershipRevenue = revenueData?.reduce((acc, curr) => acc + Number(curr.price), 0) || 0;
      lastMonthMembershipRevenue = 0;

      currentMonthSalesRevenue = salesData?.reduce((acc: number, curr: any) => acc + Number(curr.total_amount), 0) || 0;
      lastMonthSalesRevenue = 0;

      currentMonthExpenses = inventoryData?.reduce((acc, curr) => acc + (Number(curr.cost_per_unit || 0) * Number(curr.quantity || 0)), 0) || 0;
      currentMonthGeneralExpenses = expensesData?.reduce((acc, curr) => acc + Number(curr.amount), 0) || 0;
    } else {
      clientsThisMonth = clients?.filter(c => c.created_at >= startOfCurrentMonth).length || 0;
      clientsLastMonth = clients?.filter(c => c.created_at >= startOfLastMonth && c.created_at < startOfCurrentMonth).length || 0;

      currentMonthMembershipRevenue = revenueData?.filter(r => r.start_date >= startOfCurrentMonth).reduce((acc, curr) => acc + Number(curr.price), 0) || 0;
      lastMonthMembershipRevenue = revenueData?.filter(r => r.start_date < startOfCurrentMonth).reduce((acc, curr) => acc + Number(curr.price), 0) || 0;

      currentMonthSalesRevenue = salesData?.filter((s: any) => s.created_at >= startOfCurrentMonth).reduce((acc: number, curr: any) => acc + Number(curr.total_amount), 0) || 0;
      lastMonthSalesRevenue = salesData?.filter((s: any) => s.created_at < startOfCurrentMonth).reduce((acc: number, curr: any) => acc + Number(curr.total_amount), 0) || 0;

      currentMonthExpenses = inventoryData?.filter(i => i.created_at >= startOfCurrentMonth).reduce((acc, curr) => acc + (Number(curr.cost_per_unit || 0) * Number(curr.quantity || 0)), 0) || 0;
      currentMonthGeneralExpenses = expensesData?.filter(e => e.date >= startOfCurrentMonth).reduce((acc, curr) => acc + Number(curr.amount), 0) || 0;
    }

    const currentMonthRevenue = currentMonthMembershipRevenue + currentMonthSalesRevenue;
    const lastMonthRevenue = lastMonthMembershipRevenue + lastMonthSalesRevenue;

    const activeCount = new Set((activeMembers || []).map((m: any) => m.client_id)).size;

    // Growth calculation helper
    const calculateGrowth = (current: number, previous: number) => {
      if (!previous) return current > 0 ? 100 : 0;
      return Math.round(((current - previous) / previous) * 100);
    };

    // Group weekly activity
    const activityMap: Record<string, number> = {};
    const daysToMap = isFiltered ? [] : Array.from({ length: 7 }, (_, i) => addDaysToDateKey(todayKey, -i));

    if (isFiltered) {
      (weeklyData || []).forEach((ci: any) => {
        const dateStr = dateKeyInTimeZone(ci.created_at, tz);
        activityMap[dateStr] = (activityMap[dateStr] || 0) + 1;
      });
    } else {
      daysToMap.forEach(d => { activityMap[d] = 0; });
      (weeklyData || []).forEach((ci: any) => {
        const dateStr = dateKeyInTimeZone(ci.created_at, tz);
        if (activityMap[dateStr] !== undefined) {
          activityMap[dateStr]++;
        }
      });
    }

    const weeklyActivity = Object.entries(activityMap)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Calculate Member Alerts (not date-dependent)
    let membershipsQuery = supabase.from('memberships').select('status, end_date, sessions_remaining').eq('status', 'active');
    if (gymId) membershipsQuery = membershipsQuery.eq('gym_id', gymId);

    const { data: mData } = await membershipsQuery;
    const today = todayKey;
    const threeDaysFromNow = addDaysToDateKey(todayKey, 3);

    const stats = {
      totalClients,
      clientsThisMonth,
      clientsGrowth: isFiltered ? 0 : calculateGrowth(clientsThisMonth, clientsLastMonth),
      activeMembers: activeCount,
      membersGrowth: isFiltered ? 0 : calculateGrowth(clientsThisMonth, clientsLastMonth),
      revenue: currentMonthRevenue,
      revenueGrowth: isFiltered ? 0 : calculateGrowth(currentMonthRevenue, lastMonthRevenue),
      membershipRevenue: currentMonthMembershipRevenue,
      salesRevenue: currentMonthSalesRevenue,
      inventoryExpenses: currentMonthExpenses,
      generalExpenses: currentMonthGeneralExpenses,
      totalExpenses: currentMonthExpenses + currentMonthGeneralExpenses,
      netRevenue: currentMonthRevenue - currentMonthExpenses - currentMonthGeneralExpenses,
      todayCheckIns: checkinsToday || 0,
      totalCheckIns: totalCheckIns || 0,
      checkInsGrowth: isFiltered ? 0 : calculateGrowth(checkinsToday || 0, checkinsYesterday || 0),
      weeklyActivity,
      expiringToday: mData?.filter(m => m.end_date === today).length || 0,
      expiringSoon: mData?.filter(m => m.end_date > today && m.end_date <= threeDaysFromNow).length || 0,
      lowSessions: mData?.filter(m => m.sessions_remaining !== null && m.sessions_remaining <= 2).length || 0,
      isFiltered,
    } as DashboardStats;
    return { success: true, stats };
  },

  listClients: async (_token: string | null, gymId?: string) => {
    let query = supabase.from('clients').select('*').order('created_at', { ascending: false });
    if (gymId) {
      query = query.eq('gym_id', gymId);
    }
    const { data, error } = await query;
    if (error) throw error;
    return { success: true, clients: data.map(mapClient) };
  },

  createClient: async (client: any, _token: string | null, gymId?: string) => {
    // Split name
    const [first, ...rest] = client.name.split(' ');
    const last = rest.join(' ') || '.';
    const sanitizedPhone = client.phone ? sanitizePhone(client.phone) : null;

    const { data, error } = await supabase.from('clients').insert({
      gym_id: gymId || null,
      first_name: first,
      last_name: last,
      email: client.email || null,
      phone: sanitizedPhone,
      age: client.age ? Number(client.age) : null,
      sex: client.sex || null,
      status: client.status,
      qr_code: client.qr_code || `GP-${Math.random().toString(36).substring(2, 10).toUpperCase()}`, // Standardized QR Gen (Matching JoinGymPage format)
      emergency_contact: client.emergency_contact || null,
      notes: client.notes || null
    }).select().single();

    if (error) return handleApiError(error);
    return { success: true, client: mapClient(data) };
  },

  updateClient: async (client: any, _token: string | null, gymId?: string) => {
    // Split name if changed
    const sanitizedPhone = client.phone ? sanitizePhone(client.phone) : null;
    const updates: any = {
      email: client.email || null,
      phone: sanitizedPhone,
      age: client.age ? Number(client.age) : null,
      sex: client.sex || null,
      status: client.status,
      emergency_contact: client.emergency_contact || null,
      notes: client.notes || null
    };
    if (client.name) {
      const [first, ...rest] = client.name.split(' ');
      updates.first_name = first;
      updates.last_name = rest.join(' ') || '.';
    }

    let query = supabase.from('clients').update(updates).eq('id', client.client_id);
    if (gymId) {
      query = query.eq('gym_id', gymId);
    }
    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  deleteClient: async (clientId: string, _token: string | null, gymId?: string) => {
    let query = supabase.from('clients').delete().eq('id', clientId);
    if (gymId) {
      query = query.eq('gym_id', gymId);
    }
    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  // --- CLIENT PROFILE PHOTO (client-side compression + private bucket) ---
  uploadClientPhoto: async (clientId: string, file: File, gymId?: string) => {
    if (!gymId) return { success: false, error: 'Gimnasio no seleccionado' };

    const compressed = await compressImage(file);
    const ext = extensionForMime(compressed.type);
    const path = `${gymId}/${clientId}.${ext}`;

    const { error } = await supabase.storage
      .from('client_photos')
      .upload(path, compressed, { upsert: true, contentType: compressed.type });

    if (error) return handleApiError(error);

    // Elimina el archivo anterior si venía de otra ruta (p. ej. token público)
    const { data: current } = await supabase
      .from('clients')
      .select('photo_path')
      .eq('id', clientId)
      .eq('gym_id', gymId)
      .maybeSingle();
    if (current?.photo_path && current.photo_path !== path) {
      await supabase.storage.from('client_photos').remove([current.photo_path]);
    }

    const { error: updateError } = await supabase
      .from('clients')
      .update({ photo_path: path })
      .eq('id', clientId)
      .eq('gym_id', gymId);

    if (updateError) return handleApiError(updateError);

    return { success: true, photo_path: path };
  },

  /**
   * Sube la foto de una clienta a una ruta ya autorizada (flujo público).
   * La política de storage para `anon` solo permite esta operación cuando
   * el RPC de alta emitió un token de carga válido.
   */
  uploadClientPhotoToPath: async (path: string, file: File) => {
    const compressed = await compressImage(file, { fileType: 'image/jpeg' });
    const { error } = await supabase.storage
      .from('client_photos')
      .upload(path, compressed, { upsert: false, contentType: 'image/jpeg' });

    if (error) return handleApiError(error);
    return { success: true };
  },

  deleteClientPhoto: async (clientId: string, gymId?: string) => {
    if (!gymId) return { success: false, error: 'Gimnasio no seleccionado' };

    const { data: client } = await supabase
      .from('clients')
      .select('photo_path')
      .eq('id', clientId)
      .eq('gym_id', gymId)
      .maybeSingle();

    const path = client?.photo_path;
    if (path) {
      await supabase.storage.from('client_photos').remove([path]);
    }

    const { error } = await supabase
      .from('clients')
      .update({ photo_path: null })
      .eq('id', clientId)
      .eq('gym_id', gymId);

    if (error) throw error;
    return { success: true };
  },

  getClientPhotoUrl: async (photoPath: string, expiresIn = 3600) => {
    const { data, error } = await supabase.storage
      .from('client_photos')
      .createSignedUrl(photoPath, expiresIn);
    if (error) throw error;
    return { success: true, url: data.signedUrl };
  },

  listMemberships: async (_token: string | null, gymId?: string) => {
    let query = supabase.from('memberships').select('*');
    if (gymId) query = query.eq('gym_id', gymId);
    const { data, error } = await query;
    if (error) throw error;
    return { success: true, memberships: (data || []).map(mapMembership) };
  },

  listRecentRenewals: async (_token: string | null, gymId?: string) => {
    const eightDaysAgo = new Date();
    eightDaysAgo.setDate(eightDaysAgo.getDate() - 8);
    eightDaysAgo.setHours(0, 0, 0, 0);

    let query = supabase.from('memberships')
      .select('*, clients(first_name, last_name)')
      .eq('payment_status', 'paid')
      .gte('paid_at', eightDaysAgo.toISOString())
      .order('paid_at', { ascending: false });

    if (gymId) query = query.eq('gym_id', gymId);
    const { data, error } = await query;
    if (error) throw error;

    return {
      success: true,
      renewals: (data || []).map((row: any) => ({
        id: row.id,
        client_id: row.client_id,
        client_name: row.clients
          ? cleanName(row.clients.first_name, row.clients.last_name)
          : 'Desconocido',
        type: row.type,
        price: Number(row.price),
        payment_method: row.payment_method || 'cash',
        paid_at: row.paid_at,
        start_date: row.start_date,
        end_date: row.end_date,
      })),
    };
  },

  createMembership: async (membership: any, _token: string | null, gymId?: string) => {
    const payload: any = {
      gym_id: gymId || null,
      client_id: membership.client_id,
      type: membership.type,
      start_date: membership.start_date,
      end_date: membership.end_date,
      price: membership.price,
      status: membership.status,
      payment_status: membership.payment_status || 'pending',
      payment_method: membership.payment_method || 'cash',
      sessions_remaining: membership.sessions_remaining
    };
    if (membership.payment_status === 'paid') {
      payload.paid_at = new Date().toISOString();
    }
    const { data, error } = await supabase.from('memberships').insert(payload).select().single();
    if (error) return handleApiError(error);
    return { success: true, membership: mapMembership(data) };
  },

  updateMembership: async (membership: any, _token: string | null, gymId?: string) => {
    const updates: any = {
      client_id: membership.client_id,
      type: membership.type,
      start_date: membership.start_date,
      end_date: membership.end_date,
      price: membership.price,
      status: membership.status,
      payment_status: membership.payment_status,
      payment_method: membership.payment_method,
      sessions_remaining: membership.sessions_remaining
    };

    let query = supabase.from('memberships').update(updates).eq('id', membership.membership_id);
    if (gymId) query = query.eq('gym_id', gymId);
    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  renewMembership: async (data: {
    gym_id: string;
    client_id: string;
    type: string;
    start_date: string;
    end_date: string;
    price: number;
    payment_method: 'cash' | 'card' | 'transfer';
    user_id: string;
    sessions_remaining?: number | null;
    requireCashBox?: boolean;
  }, gymId?: string) => {
    const gId = gymId || data.gym_id;

    const { data: activeBox } = await supabase
      .from('cash_boxes')
      .select('id')
      .eq('gym_id', gId)
      .eq('status', 'open')
      .maybeSingle();

    if (data.requireCashBox && !activeBox) {
      return { success: false, error: 'Debe abrir una caja para renovar membresías.' };
    }

    const payload: any = {
      gym_id: gId,
      client_id: data.client_id,
      type: data.type,
      start_date: data.start_date,
      end_date: data.end_date,
      price: data.price,
      status: 'active',
      payment_status: 'paid',
      payment_method: data.payment_method,
      paid_at: new Date().toISOString(),
      sessions_remaining: data.sessions_remaining ?? null,
    };

    const { data: membership, error } = await supabase
      .from('memberships')
      .insert(payload)
      .select()
      .single();

    if (error) return handleApiError(error);

    if (data.payment_method === 'cash' && activeBox) {
      try {
        await gymApi.addCashMovement(
          activeBox.id,
          data.price,
          'in',
          `Pago membresía: ${data.type}`
        );
      } catch (e) {
        console.error('Error creating cash movement:', e);
      }
    }

    try {
      await supabase.from('audit_logs').insert({
        gym_id: gId,
        user_id: data.user_id,
        action: 'RENEW_MEMBERSHIP',
        entity_type: 'membership',
        entity_id: membership.id,
        new_data: {
          client_id: data.client_id,
          type: data.type,
          price: data.price,
          payment_method: data.payment_method,
          period: `${data.start_date} → ${data.end_date}`,
        }
      });
    } catch (e) {
      console.error('Error creating audit log:', e);
    }

    return { success: true, membership: mapMembership(membership) };
  },

  deleteMembership: async (membershipId: string, _token: string | null, gymId?: string) => {
    let query = supabase.from('memberships').delete().eq('id', membershipId);
    if (gymId) query = query.eq('gym_id', gymId);
    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  listInventory: async (_token: string | null, gymId?: string) => {
    let query = supabase.from('inventory').select('*');
    if (gymId) query = query.eq('gym_id', gymId);
    const { data, error } = await query;
    if (error) throw error;
    return { success: true, items: data.map(mapInventory) };
  },

  createInventoryItem: async (item: any, _token: string | null, gymId?: string) => {
    const { data, error } = await supabase.from('inventory').insert({
      gym_id: gymId || null,
      item_name: item.name,
      category: item.category,
      quantity: item.quantity,
      min_stock: item.min_stock,
      unit: item.unit,
      cost_per_unit: item.cost_per_unit,
      selling_price: item.selling_price,
      supplier: item.supplier,
      notes: item.notes
    }).select().single();
    if (error) throw error;
    return { success: true, item: mapInventory(data) };
  },

  updateInventoryItem: async (item: any, _token: string | null, gymId?: string) => {
    let query = supabase.from('inventory').update({
      item_name: item.name,
      category: item.category,
      quantity: item.quantity,
      min_stock: item.min_stock,
      unit: item.unit,
      cost_per_unit: item.cost_per_unit,
      selling_price: item.selling_price,
      supplier: item.supplier,
      notes: item.notes
    }).eq('id', item.item_id);

    if (gymId) query = query.eq('gym_id', gymId);
    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  deleteInventoryItem: async (itemId: string, _token: string | null, gymId?: string) => {
    let query = supabase.from('inventory').delete().eq('id', itemId);
    if (gymId) query = query.eq('gym_id', gymId);
    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  listClasses: async (_token: string | null, gymId?: string) => {
    let query = supabase.from('classes').select('*, class_bookings(count)');
    if (gymId) query = query.eq('gym_id', gymId);
    const { data, error } = await query;
    if (error) throw error;
    return { success: true, classes: data.map(mapClass) };
  },

  createClass: async (cls: any, _token: string | null, gymId?: string) => {
    console.log('[createClass] Attempting to create class:', { ...cls, gym_id: gymId });

    const insertPayload = {
      gym_id: gymId || null,
      name: cls.name,
      description: cls.description,
      instructor: cls.instructor,
      start_time: cls.start_time,
      end_time: cls.end_time,
      day_of_week: cls.day_of_week?.toString() || "1",
      capacity: cls.capacity,
      room: cls.room,
      status: 'active'
    };

    console.log('[createClass] Insert payload:', insertPayload);

    const { data, error } = await supabase
      .from('classes')
      .insert(insertPayload)
      .select()
      .single();

    if (error) return handleApiError(error);

    console.log('[createClass] Success:', data);
    return { success: true, class: mapClass(data) };
  },

  listCheckIns: async (_token: string | null, gymId?: string) => {
    let query = supabase.from('check_ins')
      .select('*, clients(first_name, last_name)')
      .order('created_at', { ascending: false })
      .limit(50);

    if (gymId) query = query.eq('gym_id', gymId);

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, check_ins: data.map(mapCheckIn) };
  },

  listTodayCheckIns: async (_token: string | null, gymId?: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString();

    let query = supabase.from('check_ins')
      .select('*, clients(first_name, last_name)')
      .gte('created_at', todayStr)
      .order('created_at', { ascending: false });

    if (gymId) query = query.eq('gym_id', gymId);

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, check_ins: data.map(mapCheckIn) };
  },

  listCheckInsByRange: async (_token: string | null, gymId: string | undefined, startDate: string, endDate: string, timeZone?: string) => {
    // Normalize the range to full days in the gym timezone (so timestamptz columns include the last day)
    const tz = timeZone || getBrowserTimeZone();
    const { startISO, endISO } = dayRangeToUtc(startDate, endDate, tz);

    let query = supabase.from('check_ins')
      .select('*, clients(first_name, last_name)')
      .gte('created_at', startISO)
      .lte('created_at', endISO)
      .order('created_at', { ascending: false });

    if (gymId) query = query.eq('gym_id', gymId);

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, check_ins: data.map(mapCheckIn) };
  },

  checkIn: async (qrCode: string, _token: string | null, gymId?: string) => {
    // 0. Auto-sync if gymId provided
    if (gymId) await gymApi.syncMemberships(gymId);

    // 1. Find Client
    let clientQuery = supabase.from('clients').select('id, first_name, last_name').eq('qr_code', qrCode);
    if (gymId) clientQuery = clientQuery.eq('gym_id', gymId);

    const { data: client } = await clientQuery.maybeSingle();

    if (!client) {
      // Record denied check-in for unknown QR
      const { error } = await supabase.from('check_ins').insert({
        gym_id: gymId || null,
        client_id: null,
        result: 'denied',
        message: 'Código QR no reconocido',
        client_name_snapshot: 'Desconocido',
        method: 'admin_scanner'
      });
      if (error) return handleApiError(error);
      return { success: true, result: 'denied', message: 'Código QR no reconocido', client_name: 'Desconocido' };
    }

    // 2. Delegate to atomic RPC for membership validation and session decrement
    const res = await gymApi.selfCheckIn(client.id, gymId!, 'admin_scanner');
    
    return res;
  },

  enrollInClass: async (classId: string, clientId: string, _token: string | null, gymId?: string) => {
    const { error } = await supabase.from('class_bookings').insert({
      gym_id: gymId || null,
      class_id: classId,
      client_id: clientId,
      status: 'confirmed'
    });

    if (error) {
      // Check for uniqueness violation or other specific errors if needed
      if (error.code === '23505') { // Unique violation
        return { success: false, error: 'El cliente ya está inscrito en esta clase' };
      }
      throw error;
    }
    return { success: true };
  },

  listClassAttendees: async (_token: string | null, classId: string, gymId?: string) => {
    // Join class_bookings with clients
    let query = supabase.from('class_bookings')
      .select('*, clients(*)')
      .eq('class_id', classId);

    if (gymId) query = query.eq('gym_id', gymId);

    const { data, error } = await query;

    if (error) throw error;

    const attendees: ClassAttendee[] = data.map((booking: any) => ({
      client_id: booking.client_id,
      client_name: booking.clients ? cleanName(booking.clients.first_name, booking.clients.last_name) : 'Desconocido',
      client_email: booking.clients?.email || '',
      client_phone: booking.clients?.phone || '',
      status: booking.status,
      enrolled_at: booking.created_at
    }));

    return { success: true, attendees };
  },

  updateClass: async (classId: string, cls: any, _token: string | null, gymId?: string) => {
    let query = supabase.from('classes').update({
      name: cls.name,
      description: cls.description,
      instructor: cls.instructor,
      start_time: cls.start_time,
      end_time: cls.end_time,
      day_of_week: cls.day_of_week?.toString(),
      capacity: cls.capacity,
      room: cls.room,
      status: cls.status
    }).eq('id', classId);

    if (gymId) query = query.eq('gym_id', gymId);

    const { data, error } = await query.select().single();
    if (error) throw error;
    return { success: true, class: mapClass(data) };
  },

  updateClassStatus: async (classId: string, status: string, _token: string | null, gymId?: string) => {
    let query = supabase.from('classes').update({ status }).eq('id', classId);
    if (gymId) query = query.eq('gym_id', gymId);
    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  deleteClass: async (classId: string, _token: string | null, gymId?: string) => {
    let query = supabase.from('classes').delete().eq('id', classId);
    if (gymId) query = query.eq('gym_id', gymId);
    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  /** Segmentación de clientas: con/sin membresía vigente y por estado. */
  getClientSegmentation: async (gymId: string) => {
    const { data, error } = await supabase.rpc('get_client_segmentation', { p_gym_id: gymId });
    if (error) return handleApiError(error);
    return { success: true, ...(data as any) };
  },

  getReportsData: async (startDate: string, endDate: string, _token: string | null, gymId?: string) => {    try {
      // Normalize the range to full local days (so timestamptz columns include the last day)
      const rangeStart = new Date(`${startDate}T00:00:00.000`);
      const rangeEnd = new Date(`${endDate}T23:59:59.999`);
      const startISO = rangeStart.toISOString();
      const endISO = rangeEnd.toISOString();

      // Previous period of equal length (for growth comparisons)
      const rangeMs = rangeEnd.getTime() - rangeStart.getTime();
      const prevEnd = new Date(rangeStart.getTime() - 1);
      const prevStart = new Date(prevEnd.getTime() - rangeMs);
      const prevStartISO = prevStart.toISOString();
      const prevEndISO = prevEnd.toISOString();

      const toDateStr = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const prevStartDate = toDateStr(prevStart);
      const prevEndDate = toDateStr(prevEnd);

      // --- Current period queries ---
      let membershipsQuery = supabase.from('memberships').select('price, payment_status, start_date').gte('start_date', startDate).lte('start_date', endDate);
      let salesQuery = supabase.from('sales').select('total_amount, created_at').gte('created_at', startISO).lte('created_at', endISO);
      let expensesQuery = supabase.from('expenses').select('amount, date').gte('date', startDate).lte('date', endDate);
      let inventoryQuery = supabase.from('inventory').select('quantity, cost_per_unit, created_at').gte('created_at', startISO).lte('created_at', endISO);
      let attendanceQuery = supabase.from('check_ins').select('result, created_at').gte('created_at', startISO).lte('created_at', endISO);
      let clientsQuery = supabase.from('clients').select('id, created_at').gte('created_at', startISO).lte('created_at', endISO);

      // --- Previous period queries ---
      let prevMembershipsQuery = supabase.from('memberships').select('price, payment_status').gte('start_date', prevStartDate).lte('start_date', prevEndDate);
      let prevSalesQuery = supabase.from('sales').select('total_amount').gte('created_at', prevStartISO).lte('created_at', prevEndISO);
      let prevClientsQuery = supabase.from('clients').select('id').gte('created_at', prevStartISO).lte('created_at', prevEndISO);

      if (gymId) {
        membershipsQuery = membershipsQuery.eq('gym_id', gymId);
        salesQuery = salesQuery.eq('gym_id', gymId);
        expensesQuery = expensesQuery.eq('gym_id', gymId);
        inventoryQuery = inventoryQuery.eq('gym_id', gymId);
        attendanceQuery = attendanceQuery.eq('gym_id', gymId);
        clientsQuery = clientsQuery.eq('gym_id', gymId);
        prevMembershipsQuery = prevMembershipsQuery.eq('gym_id', gymId);
        prevSalesQuery = prevSalesQuery.eq('gym_id', gymId);
        prevClientsQuery = prevClientsQuery.eq('gym_id', gymId);
      }

      const [membershipsRes, salesRes, expensesRes, inventoryRes, attendanceRes, clientsRes, prevMembershipsRes, prevSalesRes, prevClientsRes] = await Promise.all([
        membershipsQuery,
        salesQuery,
        expensesQuery,
        inventoryQuery,
        attendanceQuery,
        clientsQuery,
        prevMembershipsQuery,
        prevSalesQuery,
        prevClientsQuery
      ]);

      const memberships = membershipsRes.data || [];
      const sales = salesRes.data || [];
      const expenses = expensesRes.data || [];
      const inventory = inventoryRes.data || [];
      const attendance = attendanceRes.data || [];
      const clients = clientsRes.data || [];

      // Revenue = paid memberships + POS sales (same formula as the dashboard)
      const membershipRevenue = memberships
        .filter(m => m.payment_status === 'paid')
        .reduce((sum, m) => sum + Number(m.price || 0), 0);
      const salesRevenue = sales.reduce((sum, s) => sum + Number(s.total_amount || 0), 0);
      const revenue = membershipRevenue + salesRevenue;

      const generalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
      const inventoryExpenses = inventory.reduce((sum, i) => sum + Number(i.cost_per_unit || 0) * Number(i.quantity || 0), 0);
      const expensesTotal = generalExpenses + inventoryExpenses;
      const netRevenue = revenue - expensesTotal;

      const pendingRevenue = memberships
        .filter(m => m.payment_status !== 'paid')
        .reduce((sum, m) => sum + Number(m.price || 0), 0);

      const allowedCheckins = attendance.filter(c => c.result === 'allowed');
      const attendanceTotal = allowedCheckins.length;
      const deniedTotal = attendance.filter(c => c.result === 'denied').length;
      const newClientsTotal = clients.length;

      // Previous period revenue + clients for growth
      const prevMembershipRevenue = (prevMembershipsRes.data || [])
        .filter(m => m.payment_status === 'paid')
        .reduce((sum, m) => sum + Number(m.price || 0), 0);
      const prevSalesRevenue = (prevSalesRes.data || []).reduce((sum, s) => sum + Number(s.total_amount || 0), 0);
      const prevRevenue = prevMembershipRevenue + prevSalesRevenue;
      const prevNewClients = (prevClientsRes.data || []).length;

      const calculateGrowth = (current: number, previous: number): number | null => {
        if (!previous) return null;
        return Math.round(((current - previous) / previous) * 100);
      };

      // Attendance by weekday (Mon–Dom), based on valid check-ins
      const weekdayCounts = [0, 0, 0, 0, 0, 0, 0]; // Sun..Sat
      allowedCheckins.forEach(c => {
        const day = new Date(c.created_at).getDay();
        weekdayCounts[day]++;
      });
      const weekdayOrder = [1, 2, 3, 4, 5, 6, 0];
      const weekdayLabels = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
      const attendanceByWeekday = weekdayOrder.map((idx, i) => ({ label: weekdayLabels[i], count: weekdayCounts[idx] }));

      return {
        success: true,
        data: {
          revenue,
          membershipRevenue,
          salesRevenue,
          expenses: expensesTotal,
          generalExpenses,
          inventoryExpenses,
          netRevenue,
          pendingRevenue,
          attendance: attendanceTotal,
          denied: deniedTotal,
          newClients: newClientsTotal,
          revenueGrowth: calculateGrowth(revenue, prevRevenue),
          newClientsGrowth: calculateGrowth(newClientsTotal, prevNewClients),
          attendanceByWeekday,
          details: {
            revenueItems: memberships,
            salesItems: sales,
            expenseItems: expenses,
            inventoryItems: inventory,
            attendanceItems: attendance,
            clientItems: clients
          }
        }
      };
    } catch (error) {
      console.error('Failed to fetch reports data:', error);
      throw error;
    }
  },

  getExportData: async (gymId: string) => {
    try {
      const [
        { data: clients },
        { data: memberships },
        { data: sales },
        { data: expenses },
        { data: checkins }
      ] = await Promise.all([
        supabase.from('clients').select('*').eq('gym_id', gymId),
        supabase.from('memberships').select('*').eq('gym_id', gymId),
        supabase.from('sales').select('*, clients(first_name, last_name)').eq('gym_id', gymId),
        supabase.from('expenses').select('*').eq('gym_id', gymId),
        supabase.from('check_ins').select('*').eq('gym_id', gymId).order('created_at', { ascending: false }).limit(2000)
      ]);

      return {
        success: true,
        data: {
          clients: clients || [],
          memberships: memberships || [],
          sales: sales?.map(s => ({
            ...s,
            client_name: s.clients ? cleanName((s.clients as any).first_name, (s.clients as any).last_name) : 'Public'
          })) || [],
          expenses: expenses || [],
          checkins: checkins || []
        }
      };
    } catch (error) {
      console.error('Error fetching export data:', error);
      throw error;
    }
  },



  // Stubs for Webhooks/etc (Not migrated in schema yet)
  listWebhooks: async () => ({ success: true, webhooks: [] }),
  createWebhook: async () => ({ success: true }),
  updateWebhook: async () => ({ success: true }),
  deleteWebhook: async () => ({ success: true }),

  getGymBySlug: async (slug: string) => {
    // Normalize slug: toLower and remove trailing slashes for improved compatibility (especially on iOS)
    const normalizedSlug = slug.toLowerCase().replace(/\/+$/, '').trim();
    // Query gyms table directly (public_gym_info view inherits RLS without anon policy)
    const { data, error } = await supabase
      .from('gyms')
      .select('*')
      .eq('slug', normalizedSlug)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new GymNotFoundError(normalizedSlug);
    return { success: true, gym: mapGym(data) };
  },

  registerGuest: async (registration: {
    gym_id: string;
    plan_id: string;
    name: string;
    email: string;
    phone: string;
    age?: number;
  }) => {
    const sanitizedPhone = sanitizePhone(registration.phone);
    const { data, error } = await supabase.rpc('register_new_client', {
      p_gym_id: registration.gym_id,
      p_plan_id: registration.plan_id,
      p_name: registration.name,
      p_email: registration.email,
      p_phone: sanitizedPhone,
      p_age: registration.age ? Number(registration.age) : null
    });

    if (error) return handleApiError(error);

    if (data?.success === false && data?.error?.includes('duplicate key value')) {
      return { success: false, error: 'Este cliente ya está registrado en este gimnasio' };
    }

    return { success: true, ...data };
  },

  /**
   * Alta de clienta en el flujo público con foto de perfil.
   * El RPC `register_new_client_with_photo` crea la clienta y emite un token
   * de carga de un solo uso; la foto se sube a la ruta exacta que devuelve.
   */
  registerGuestWithPhoto: async (
    registration: {
      gym_id: string;
      plan_id: string;
      name: string;
      email: string;
      phone: string;
      age?: number;
    },
    photoFile?: File | null
  ) => {
    const sanitizedPhone = sanitizePhone(registration.phone);
    const { data, error } = await supabase.rpc('register_new_client_with_photo', {
      p_gym_id: registration.gym_id,
      p_plan_id: registration.plan_id,
      p_name: registration.name,
      p_email: registration.email,
      p_phone: sanitizedPhone,
      p_age: registration.age ? Number(registration.age) : null
    });

    if (error) return handleApiError(error);

    if (data?.success === false && data?.error?.includes('duplicate key value')) {
      return { success: false, error: 'Este cliente ya está registrado en este gimnasio' };
    }

    // La foto es opcional: si falla la subida, el alta sigue siendo válida.
    if (photoFile && data?.photo_upload_path) {
      try {
        const upload = await gymApi.uploadClientPhotoToPath(data.photo_upload_path, photoFile);
        if (upload.success) {
          await supabase.rpc('consume_client_photo_token', { p_token: data.photo_upload_token });
        }
      } catch (err) {
        console.warn('[registerGuestWithPhoto] no se pudo subir la foto:', err);
      }
    }

    return { success: true, ...data };
  },

  /**
   * Importación masiva de clientas desde Excel.
   * El lote se procesa en el servidor (`import_clients_batch_staff`); el
   * `gym_id` lo impone el servidor, nunca el cliente.
   */
  importClientsBatch: async (
    gymId: string,
    rows: Record<string, any>[],
    mode: 'create' | 'upsert' = 'create'
  ) => {
    const { data, error } = await supabase.rpc('import_clients_batch_staff', {
      p_gym_id: gymId,
      p_rows: rows as any,
      p_mode: mode,
    });

    if (error) return handleApiError(error);
    return { success: true, ...(data as any) };
  },

  /** Importación masiva de membresías desde Excel. */
  importMembershipsBatch: async (gymId: string, rows: Record<string, any>[]) => {
    const { data, error } = await supabase.rpc('import_memberships_batch_staff', {
      p_gym_id: gymId,
      p_rows: rows as any,
    });

    if (error) return handleApiError(error);
    return { success: true, ...(data as any) };
  },

  listSales: async (gymId: string) => {
    const { data, error } = await supabase
      .from('sales')
      .select('*, clients(first_name, last_name), sale_items(*, inventory(item_name))')
      .eq('gym_id', gymId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, sales: data.map(mapSale) };
  },

  createSale: async (sale: {
    gym_id: string;
    client_id?: string;
    total_amount: number;
    payment_method: string;
    items: { product_id: string; quantity: number; unit_price: number }[];
  }) => {
    // 1. Get active cash box
    const { data: activeBox } = await supabase
      .from('cash_boxes')
      .select('id')
      .eq('gym_id', sale.gym_id)
      .eq('status', 'open')
      .maybeSingle();

    // 2. Insert Sale
    const { data: saleData, error: saleError } = await supabase
      .from('sales')
      .insert({
        gym_id: sale.gym_id,
        client_id: sale.client_id,
        total_amount: sale.total_amount,
        payment_method: sale.payment_method,
        cash_box_id: activeBox?.id || null
      })
      .select()
      .single();

    if (saleError) return handleApiError(saleError);

    // 3. Insert Sale Items
    const saleItems = sale.items.map(item => ({
      sale_id: saleData.id,
      product_id: item.product_id,
      quantity: item.quantity,
      unit_price: item.unit_price
    }));

    const { error: itemsError } = await supabase
      .from('sale_items')
      .insert(saleItems);

    if (itemsError) return handleApiError(itemsError);

    // 4. Integration with Cash Box (If payment is cash)
    if (sale.payment_method === 'cash' && activeBox) {
      await gymApi.addCashMovement(
        activeBox.id,
        sale.total_amount,
        'in',
        `Venta de productos (ID: ${saleData.id.substring(0, 8)})`
      );
    }

    return { success: true, sale_id: saleData.id };
  },

  listMembershipPlans: async (gymId?: string, includePrivate: boolean = true) => {
    let query = supabase.from('membership_plans').select('*');
    if (gymId) {
      // If gymId is provided, ONLY return plans for that gym. 
      // This fulfills the requirement of "exclusive local memberships".
      query = query.eq('gym_id', gymId);
    } else {
      // If no gymId (Super Admin global view), only show "global" plans (null gym_id)
      query = query.is('gym_id', null);
    }

    if (!includePrivate) {
      query = query.eq('is_private', false);
    }

    const { data, error } = await query.order('price', { ascending: true });
    if (error) throw error;
    return { success: true, plans: data.map(mapPlan) };
  },

  createMembershipPlan: async (plan: any) => {
    const { data, error } = await supabase
      .from('membership_plans')
      .insert(plan)
      .select()
      .single();
    if (error) throw error;
    return { success: true, plan: mapPlan(data) };
  },

  updateMembershipPlan: async (planId: string, updates: any) => {
    const { data, error } = await supabase
      .from('membership_plans')
      .update(updates)
      .eq('id', planId)
      .select()
      .single();
    if (error) throw error;
    return { success: true, plan: mapPlan(data) };
  },

  deleteMembershipPlan: async (planId: string) => {
    const { error } = await supabase
      .from('membership_plans')
      .delete()
      .eq('id', planId);
    if (error) throw error;
    return { success: true };
  },

  getExpiringMemberships: async (gymId: string, daysAhead: number = 7) => {
    // 1. Sync statuses first (ensure expired ones are marked 'vencida')
    await gymApi.syncMemberships(gymId);

    const today = new Date().toISOString().split('T')[0];
    const futureDate = new Date(Date.now() + daysAhead * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    // Fetch memberships that are expired (end_date < today) or expiring soon (end_date <= futureDate)
    const { data, error } = await supabase
      .from('memberships')
      .select('*, clients(first_name, last_name)')
      .eq('gym_id', gymId)
      .eq('status', 'active')
      .lte('end_date', futureDate)
      .order('end_date', { ascending: true });

    if (error) throw error;

    const memberships = (data || []).map((m: any) => ({
      ...mapMembership(m),
      client_name: m.clients ? cleanName(m.clients.first_name, m.clients.last_name) : 'Desconocido',
      is_expired: m.end_date < today,
    }));
    return { success: true, memberships };
  },

  syncMemberships: async (gymId: string) => {
    const { error } = await supabase.rpc('sync_membership_statuses', { p_gym_id: gymId });
    if (error) throw error;
    return { success: true };
  },


  findClientByIdentifier: async (gymId: string, identifier: string) => {
    // Sanitize identifier if it looks like a phone number (e.g. contains digits, spaces, hyphens)
    // But avoid sanitizing if it's already a clean QR code (starts with GP-) or UUID
    let processedIdentifier = identifier.trim();
    if (/^[\d\s\-+]+$/.test(processedIdentifier)) {
      // Just extract digits for smart matching in the RPC
      processedIdentifier = processedIdentifier.replace(/\D/g, '');
    }

    // USE RPC for public search (avoids RLS blocking anon users)
    const { data, error } = await supabase
      .rpc('search_client_public', {
        p_gym_id: gymId,
        p_identifier: processedIdentifier
      });

    if (error) throw error;

    // The RPC returns a table which is an array in JS
    return { success: true, clients: (data || []).map(mapClient) };
  },

  selfCheckIn: async (
    clientId: string,
    gymId: string,
    method: string = 'self_checkin',
    deviceId?: string | null,
    latitude?: number | null,
    longitude?: number | null
  ) => {
    // USE RPC for secure processing (atomic and bypasses RLS for check-in record)
    // The 6-arg overload adds device binding + geofence enforcement server-side.
    const { data, error } = await supabase
      .rpc('process_self_check_in', {
        p_client_id: clientId,
        p_gym_id: gymId,
        p_method: method,
        p_device_id: deviceId ?? null,
        p_latitude: latitude ?? null,
        p_longitude: longitude ?? null
      });

    if (error) throw error;
    return data; // This already contains { success, result, message, client_name, sessions_remaining }
  },

  getClientDevice: async (gymId: string, clientId: string) => {
    const { data, error } = await supabase
      .from('client_devices')
      .select('device_id, device_label, bound_at, last_seen_at')
      .eq('gym_id', gymId)
      .eq('client_id', clientId)
      .is('revoked_at', null)
      .maybeSingle();

    if (error) throw error;
    return { success: true, device: data };
  },

  resetClientDevice: async (gymId: string, clientId: string) => {
    const { data, error } = await supabase
      .rpc('reset_client_device_staff', {
        p_gym_id: gymId,
        p_client_id: clientId
      });

    if (error) throw error;
    return data;
  },

  getClientCheckIns: async (clientId: string, limit: number = 10) => {
    const { data, error } = await supabase
      .from('check_ins')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return { success: true, check_ins: data.map(mapCheckIn) };
  },

  getClientPaymentHistory: async (clientId: string, gymId: string): Promise<{ success: boolean; transactions: PaymentTransaction[]; totalSpent: number }> => {
    const [
      { data: memberships },
      { data: sales }
    ] = await Promise.all([
      supabase
        .from('memberships')
        .select('id, type, price, paid_at, start_date, end_date')
        .eq('client_id', clientId)
        .eq('gym_id', gymId)
        .eq('payment_status', 'paid')
        .order('paid_at', { ascending: false }),
      supabase
        .from('sales')
        .select('id, total_amount, payment_method, created_at, sale_items(quantity, unit_price, inventory(item_name))')
        .eq('client_id', clientId)
        .eq('gym_id', gymId)
        .order('created_at', { ascending: false })
    ]);

    const membershipTransactions: PaymentTransaction[] = (memberships || []).map((m: any) => ({
      id: m.id,
      type: 'membership' as const,
      date: m.paid_at || m.created_at,
      amount: Number(m.price),
      description: m.type,
      membership_type: m.type,
      membership_start: m.start_date,
      membership_end: m.end_date
    }));

    const saleTransactions: PaymentTransaction[] = (sales || []).map((s: any) => ({
      id: s.id,
      type: 'sale' as const,
      date: s.created_at,
      amount: Number(s.total_amount),
      description: s.sale_items?.map((si: any) => `${si.quantity}x ${si.inventory?.item_name || 'Producto'}`).join(', ') || 'Venta',
      payment_method: s.payment_method,
      items: (s.sale_items || []).map((si: any) => ({
        product_name: si.inventory?.item_name || 'Producto',
        quantity: si.quantity,
        unit_price: Number(si.unit_price)
      }))
    }));

    const allTransactions = [...membershipTransactions, ...saleTransactions]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const totalSpent = allTransactions.reduce((sum, t) => sum + t.amount, 0);

    return { success: true, transactions: allTransactions, totalSpent };
  },

  updateAlertPreference: async (userId: string, disabled: boolean) => {
    const { error } = await supabase
      .from('profiles')
      .update({ alerts_disabled: disabled })
      .eq('id', userId);
    if (error) throw error;
    return { success: true };
  },

  listExpenses: async (gymId: string) => {
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('gym_id', gymId)
      .order('date', { ascending: false });
    if (error) throw error;
    return { success: true, expenses: data as Expense[] };
  },

  createExpense: async (expense: Partial<Expense>) => {
    const { data, error } = await supabase
      .from('expenses')
      .insert(expense)
      .select()
      .single();
    if (error) throw error;
    return { success: true, expense: data as Expense };
  },

  updateExpense: async (expenseId: string, updates: Partial<Expense>) => {
    const { data, error } = await supabase
      .from('expenses')
      .update(updates)
      .eq('id', expenseId)
      .select()
      .single();
    if (error) throw error;
    return { success: true, expense: data as Expense };
  },

  deleteExpense: async (expenseId: string) => {
    const { error } = await supabase
      .from('expenses')
      .delete()
      .eq('id', expenseId);
    if (error) throw error;
    return { success: true };
  },

  // --- CASH BOX / PETTY CASH ---
  getActiveBox: async (gymId: string) => {
    const { data, error } = await supabase
      .from('cash_boxes')
      .select('*, cash_movements(*)')
      .eq('gym_id', gymId)
      .eq('status', 'open')
      .maybeSingle();

    if (error) throw error;
    return { success: true, box: data as (CashBox & { cash_movements: CashMovement[] }) | null };
  },

  openBox: async (gymId: string, userId: string, openingBalance: number) => {
    const { data, error } = await supabase
      .from('cash_boxes')
      .insert({
        gym_id: gymId,
        user_id: userId,
        opening_balance: openingBalance,
        expected_balance: openingBalance,
        status: 'open',
        opened_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, box: data as CashBox };
  },

  closeBox: async (boxId: string, userId: string, actualBalance: number, notes?: string) => {
    const { data: current, error: fetchError } = await supabase
      .from('cash_boxes')
      .select('status')
      .eq('id', boxId)
      .single();

    if (fetchError) throw fetchError;
    if (!current || current.status !== 'open') {
      throw new Error('La caja ya está cerrada o no existe.');
    }

    const { data, error } = await supabase
      .from('cash_boxes')
      .update({
        actual_balance: actualBalance,
        status: 'closed',
        closed_at: new Date().toISOString(),
        closed_by: userId,
        notes: notes
      })
      .eq('id', boxId)
      .eq('status', 'open')
      .select()
      .single();

    if (error) throw error;
    return { success: true, box: data as CashBox };
  },

  addCashMovement: async (boxId: string, amount: number, type: 'in' | 'out', reason: string) => {
    // 1. Insert Movement
    const { data: movement, error: moveError } = await supabase
      .from('cash_movements')
      .insert({
        cash_box_id: boxId,
        amount,
        type,
        reason
      })
      .select()
      .single();

    if (moveError) throw moveError;

    // 2. Update expected_balance in Cash Box
    const change = type === 'in' ? amount : -amount;
    const { error: updateError } = await supabase.rpc('update_cash_box_balance', {
      p_box_id: boxId,
      p_amount_change: change
    });

    if (updateError) {
      // Fallback if RPC doesn't exist yet (though it should ideally)
      const { data: currentBox } = await supabase.from('cash_boxes').select('expected_balance').eq('id', boxId).single();
      if (currentBox) {
        await supabase.from('cash_boxes').update({
          expected_balance: Number(currentBox.expected_balance) + change
        }).eq('id', boxId);
      }
    }

    return { success: true, movement: movement as CashMovement };
  },

  getBoxHistory: async (gymId: string) => {
    const { data, error } = await supabase
      .from('cash_boxes')
      .select('*, cash_movements(*)')
      .eq('gym_id', gymId)
      .eq('status', 'closed')
      .order('closed_at', { ascending: false })
      .limit(30);

    if (error) throw error;
    return { success: true, history: (data || []) as (CashBox & { cash_movements: CashMovement[] })[] };
  },

  // --- REWARDS (staff, tenant-scoped) ---
  listRewards: async (gymId: string) => {
    const { data, error } = await supabase
      .from('rewards')
      .select('*, reward_prizes(*), reward_raffle_entries(*, clients(first_name, last_name))')
      .eq('gym_id', gymId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return { success: true, rewards: (data || []).map(mapReward) };
  },

  createReward: async (reward: Partial<Reward>, prizes: Partial<RewardPrize>[] = []) => {
    const { prizes: _ignored, ...rewardFields } = (reward || {}) as any;
    const { data, error } = await supabase
      .from('rewards')
      .insert(rewardFields)
      .select()
      .single();
    if (error) return handleApiError(error);

    if (prizes && prizes.length > 0) {
      const prizeRows = prizes.map((p, idx) => ({
        reward_id: data.id,
        gym_id: data.gym_id,
        label: p.label || 'Premio',
        weight: p.weight ?? 1,
        stock: p.stock ?? null,
        remaining: p.stock ?? null,
        sort_order: p.sort_order ?? idx,
        is_active: p.is_active ?? true,
      }));
      const { error: prizeError } = await supabase.from('reward_prizes').insert(prizeRows);
      if (prizeError) return handleApiError(prizeError);
    }

    return { success: true, reward_id: data.id, reward: mapReward(data) };
  },

  updateReward: async (rewardId: string, updates: Partial<Reward>, prizes?: Partial<RewardPrize>[]) => {
    const { prizes: _ignored, ...rewardFields } = (updates || {}) as any;
    const { data, error } = await supabase
      .from('rewards')
      .update(rewardFields)
      .eq('id', rewardId)
      .select()
      .single();
    if (error) return handleApiError(error);

    if (prizes) {
      const { error: delError } = await supabase.from('reward_prizes').delete().eq('reward_id', rewardId);
      if (delError) return handleApiError(delError);
      if (prizes.length > 0) {
        const prizeRows = prizes.map((p, idx) => ({
          reward_id: rewardId,
          gym_id: data.gym_id,
          label: p.label || 'Premio',
          weight: p.weight ?? 1,
          stock: p.stock ?? null,
          remaining: p.stock ?? null,
          sort_order: p.sort_order ?? idx,
          is_active: p.is_active ?? true,
        }));
        const { error: insError } = await supabase.from('reward_prizes').insert(prizeRows);
        if (insError) return handleApiError(insError);
      }
    }

    return { success: true, reward: mapReward(data) };
  },

  setRewardActive: async (rewardId: string, isActive: boolean) => {
    const { error } = await supabase
      .from('rewards')
      .update({ is_active: isActive })
      .eq('id', rewardId);
    if (error) throw error;
    return { success: true };
  },

  deleteReward: async (rewardId: string) => {
    const { error } = await supabase
      .from('rewards')
      .delete()
      .eq('id', rewardId);
    if (error) throw error;
    return { success: true };
  },

  listRewardParticipations: async (gymId: string, limit: number = 100) => {
    const { data, error } = await supabase
      .from('reward_participations')
      .select('*, rewards(title), reward_prizes(label), clients(first_name, last_name)')
      .eq('gym_id', gymId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return { success: true, participations: (data || []).map(mapRewardParticipation) };
  },

  // --- REWARDS access code (staff) ---
  getRewardAccessCode: async (gymId: string): Promise<RewardAccessCode> => {
    const { data, error } = await supabase.rpc('get_reward_access_code_staff', {
      p_gym_id: gymId,
    });
    if (error) throw error;
    return data as RewardAccessCode;
  },

  regenerateRewardAccessCode: async (gymId: string): Promise<RewardAccessCode> => {
    const { data, error } = await supabase.rpc('regenerate_reward_access_code_staff', {
      p_gym_id: gymId,
    });
    if (error) throw error;
    return data as RewardAccessCode;
  },

  // --- REWARDS (public member flow, via SECURITY DEFINER RPCs) ---
  hasActiveRewards: async (gymId: string, clientId: string) => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { data, error } = await supabase.rpc('has_active_rewards_public', {
          p_gym_id: gymId,
          p_client_id: clientId,
        });
        if (error) throw error;
        return {
          success: data?.success !== false,
          hasRewards: !!data?.has_rewards,
          codeRequired: data?.code_required !== false,
        };
      } catch (error) {
        if (!isNetworkRpcError(error)) throw error;
        if (attempt === 0) {
          await sleep(800);
          continue;
        }
        return { success: false, hasRewards: false, codeRequired: true };
      }
    }
    return { success: false, hasRewards: false, codeRequired: true };
  },

  verifyRewardCode: async (gymId: string, clientId: string, code: string): Promise<RewardCodeVerification> => {
    const { data, error } = await supabase.rpc('verify_reward_access_code_public', {
      p_gym_id: gymId,
      p_client_id: clientId,
      p_code: code,
    });
    if (error) throw error;
    return (data || { success: false, reason: 'invalid' }) as RewardCodeVerification;
  },

  getClientRewards: async (gymId: string, clientId: string, code: string) => {
    const { data, error } = await supabase.rpc('get_client_rewards_with_code_public', {
      p_gym_id: gymId,
      p_client_id: clientId,
      p_code: code,
    });
    if (error) throw error;

    if (!data || data.success === false) {
      return {
        success: false,
        reason: (data?.reason || 'invalid') as string,
        message: data?.message as string | undefined,
        retryAfterSeconds: data?.retry_after_seconds as number | undefined,
        attemptsRemaining: data?.attempts_remaining as number | undefined,
        rewards: [] as ClientReward[],
        validUntil: undefined as string | undefined,
      };
    }

    const list = Array.isArray(data.rewards) ? data.rewards : [];
    return {
      success: true,
      reason: undefined as string | undefined,
      message: undefined as string | undefined,
      retryAfterSeconds: undefined as number | undefined,
      attemptsRemaining: undefined as number | undefined,
      rewards: list.map(mapClientReward),
      validUntil: data.valid_until as string | undefined,
    };
  },

  playWheel: async (gymId: string, clientId: string, rewardId: string, code: string) => {
    const { data, error } = await supabase.rpc('play_wheel_with_code_public', {
      p_gym_id: gymId,
      p_client_id: clientId,
      p_reward_id: rewardId,
      p_code: code,
    });
    if (error) throw error;
    return data; // { success, result, message, reason, prize_id, prize_label, segment_index }
  },

  markRewardSeen: async (gymId: string, clientId: string, rewardId: string, code: string) => {
    const { data, error } = await supabase.rpc('mark_reward_seen_with_code_public', {
      p_gym_id: gymId,
      p_client_id: clientId,
      p_reward_id: rewardId,
      p_code: code,
    });
    if (error) throw error;
    return data || { success: true };
  },

  // --- REWARDS raffle (staff) ---
  listRewardProgress: async (rewardId: string) => {
    const { data, error } = await supabase
      .from('client_reward_progress')
      .select('reward_id, client_id, counted_days, manual_adjustment, manual_adjustment_at, completed_at, updated_at, clients(first_name, last_name)')
      .eq('reward_id', rewardId)
      .order('counted_days', { ascending: false })
      .order('updated_at', { ascending: true });
    if (error) throw error;
    return { success: true, progress: (data || []).map(mapRewardProgress) };
  },

  setRewardProgressDays: async (rewardId: string, clientId: string, countedDays: number) => {
    const { data, error } = await supabase.rpc('set_reward_progress_days_staff', {
      p_reward_id: rewardId,
      p_client_id: clientId,
      p_counted_days: countedDays,
    });
    if (error) throw error;
    return data; // { success, message, counted_days, base_days, manual_adjustment }
  },

  listRaffleEntries: async (rewardId: string) => {
    const { data, error } = await supabase
      .from('reward_raffle_entries')
      .select('*, clients(first_name, last_name)')
      .eq('reward_id', rewardId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return { success: true, entries: (data || []).map(mapRaffleEntry) };
  },

  drawRewardWinner: async (rewardId: string) => {
    const { data, error } = await supabase.rpc('draw_reward_winner', { p_reward_id: rewardId });
    if (error) throw error;
    return data; // { success, message, winner_client_id, entries }
  },

  markRaffleClaimed: async (rewardId: string) => {
    const { data, error } = await supabase.rpc('mark_raffle_claimed', { p_reward_id: rewardId });
    if (error) throw error;
    return data; // { success, message, winner_client_id }
  },

  // --- REWARDS leaderboard (public) ---
  getRewardLeaderboard: async (gymId: string, rewardId: string, clientId: string, code: string, limit: number = 5): Promise<RewardLeaderboard> => {
    const { data, error } = await supabase.rpc('get_reward_leaderboard_with_code_public', {
      p_gym_id: gymId,
      p_reward_id: rewardId,
      p_client_id: clientId,
      p_code: code,
      p_limit: limit,
    });
    if (error) throw error;
    if (data?.reason) return { top: [], me: null };
    return (data as RewardLeaderboard) || { top: [], me: null };
  }
};

export const cashApi = gymApi; // Alias or we can export separately

export const adminApi = {
  // Super Admin Stats
  getSuperAdminStats: async (_token: string | null) => {
    try {
      const [
        { count: gymCount },
        { count: activeGyms },
        { data: gymsData },
        { data: profilesData }
      ] = await Promise.all([
        supabase.from('gyms').select('*', { count: 'exact', head: true }),
        supabase.from('gyms').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('gyms').select('id, name, plan, modules_enabled, tier:saas_tiers(name)'),
        supabase.from('profiles').select('role')
      ]);

      // Calculate plan distribution dynamically
      const planDistribution: Record<string, number> = {};
      gymsData?.forEach(g => {
        const tierName = (g.tier as any)?.name || g.plan || 'Otros';
        planDistribution[tierName] = (planDistribution[tierName] || 0) + 1;
      });

      // Calculate user role distribution
      const userRoles: Record<string, number> = {};
      profilesData?.forEach(p => {
        userRoles[p.role] = (userRoles[p.role] || 0) + 1;
      });

      return {
        success: true,
        stats: {
          totalGyms: gymCount || 0,
          activeGyms: activeGyms || 0,
          inactiveGyms: (gymCount || 0) - (activeGyms || 0),
          expiredGyms: 0,
          expiringSoon: 0,
          planDistribution,
          userRoles,
          gymModules: gymsData?.map(g => ({
            name: g.name,
            modules: g.modules_enabled || []
          })) || []
        }
      };
    } catch (error: any) {
      console.error('[adminApi] getSuperAdminStats error:', error);
      return { success: false, error: error.message };
    }
  },

  listGyms: async () => {
    const { data, error } = await supabase.from('gyms').select('*, tier:saas_tiers(*)');
    if (error) throw error;
    return { success: true, gyms: (data || []).map(mapGym) };
  },

  // --- PLAN USAGE (super-admin only, internal monitor) ---
  getPlanUsage: async () => {
    const { data, error } = await supabase.rpc('get_plan_usage');
    if (error) throw error;
    return { success: true, usage: data };
  },

  // Egress + API request counts (not queryable via SQL) — proxied through the
  // plan-usage-metrics Edge Function to the Supabase Management API.
  getPlanUsageMetrics: async () => {
    const { data, error } = await supabase.functions.invoke('plan-usage-metrics', { body: {} });
    if (error) throw error;
    return { success: true, metrics: data };
  },

  // Per-gym resource consumption (exact storage + row counts, estimated db bytes).
  // Only params attributable to a single gym; egress is not attributable per gym.
  getGymUsage: async (gymId: string) => {
    const { data, error } = await supabase.rpc('get_gym_usage', { p_gym_id: gymId });
    if (error) throw error;
    return { success: true, usage: data };
  },

  // Per-gym REST request count (last 24h, approximate) via the Management API logs.
  getGymUsageMetrics: async (gymId: string) => {
    const { data, error } = await supabase.functions.invoke('plan-usage-metrics', {
      body: { gym_id: gymId },
    });
    if (error) throw error;
    return { success: true, metrics: data };
  },

  // Resumen de facturación electrónica (DTE) por gimnasio (super admin).
  getGymFacturacionSummary: async (gymId: string) => {
    const { data, error } = await supabase.rpc('get_gym_facturacion_summary', { p_gym_id: gymId });
    if (error) throw error;
    return { success: true, summary: data };
  },

  listSaasTiers: async () => {
    const { data, error } = await supabase
      .from('saas_tiers')
      .select('*')
      .order('price', { ascending: true });
    if (error) throw error;
    return { success: true, tiers: data };
  },

  createGym: async (data: any) => {
    console.log('[createGym] Starting provisioning for:', data.name);

    // Helper to generate slug
    const generateSlug = (name: string) => {
      return name.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '-');
    };

    // 1. Create Gym
    const gymPayload: Record<string, any> = {
      name: data.name,
      slug: generateSlug(data.name),
      country: data.country,
      city: data.city,
      timezone: data.timezone,
      phone: data.phone,
      email: data.email,
      plan: data.plan,
      price: data.price,
      billing_cycle: data.billing_cycle,
      status: data.status || 'active',
      modules_enabled: data.modules_enabled || [],
      expiry_date: data.expiry_date,
      settings: data.settings || {
        opening_hours: "06:00",
        closing_hours: "22:00",
        multiple_checkin: false,
        attendance_method: "qr",
        max_capacity: 50
      }
    };
    if (data.tier_id !== undefined) gymPayload.tier_id = data.tier_id;
    if (data.is_trial !== undefined) gymPayload.is_trial = data.is_trial;

    const { data: gym, error: gymError } = await supabase
      .from('gyms')
      .insert(gymPayload)
      .select()
      .single();

    if (gymError) {
      console.error('[createGym] Error creating gym:', gymError);
      throw gymError;
    }

    // 2. Create Admin User if data exists
    if (data.admin && data.admin.email) {
      console.log('[createGym] Provisioning admin user:', data.admin.email);
      const userRes = await adminApi.createUser({
        name: data.admin.name,
        email: data.admin.email,
        password: data.admin.password,
        role: 'gym_admin',
        gym_id: gym.id
      });

      if (!userRes.success) {
        console.error('[createGym] Error creating admin user:', userRes.error);
        // We don't rollback the gym for now, but we return the error
        return { success: true, gym, warning: 'Gimnasio creado pero falló la creación del administrador: ' + userRes.error };
      }
    }

    return { success: true, gym };
  },

  updateGym: async (gym: any) => {
    console.log('[updateGym] Attempting to update gym:', gym);

    const updatePayload: Record<string, any> = {};
    if (gym.name !== undefined) updatePayload.name = gym.name;
    if (gym.plan !== undefined) updatePayload.plan = gym.plan;
    if (gym.tier_id !== undefined) updatePayload.tier_id = gym.tier_id;
    if (gym.expiry_date !== undefined) updatePayload.expiry_date = gym.expiry_date;
    if (gym.status !== undefined) updatePayload.status = gym.status;
    if (gym.modules_enabled !== undefined) updatePayload.modules_enabled = gym.modules_enabled;
    if (gym.is_trial !== undefined) updatePayload.is_trial = gym.is_trial;
    if (gym.country !== undefined) updatePayload.country = gym.country;
    if (gym.city !== undefined) updatePayload.city = gym.city;
    if (gym.timezone !== undefined) updatePayload.timezone = gym.timezone;
    if (gym.phone !== undefined) updatePayload.phone = gym.phone;
    if (gym.email !== undefined) updatePayload.email = gym.email;
    if (gym.billing_cycle !== undefined) updatePayload.billing_cycle = gym.billing_cycle;
    if (gym.price !== undefined) updatePayload.price = gym.price;
    if (gym.settings !== undefined) updatePayload.settings = gym.settings;
    if (gym.latitude !== undefined) updatePayload.latitude = gym.latitude;
    if (gym.longitude !== undefined) updatePayload.longitude = gym.longitude;
    if (gym.address !== undefined) updatePayload.address = gym.address;
    if (gym.twilio_account_sid !== undefined) updatePayload.twilio_account_sid = gym.twilio_account_sid;
    if (gym.twilio_auth_token !== undefined) updatePayload.twilio_auth_token = gym.twilio_auth_token;
    if (gym.twilio_phone_number !== undefined) updatePayload.twilio_phone_number = gym.twilio_phone_number;
    if (gym.openai_api_key !== undefined) updatePayload.openai_api_key = gym.openai_api_key;
    if (gym.credit_balance !== undefined) updatePayload.credit_balance = gym.credit_balance;

    console.log('[updateGym] Update payload:', updatePayload, 'for gym_id:', gym.gym_id);

    const { data, error } = await supabase
      .from('gyms')
      .update(updatePayload)
      .eq('id', gym.gym_id)
      .select();

    if (error) {
      console.error('[updateGym] Supabase error:', error);
      throw error;
    }

    console.log('[updateGym] Success, updated rows:', data);
    return { success: true, data };
  },

  deleteGym: async (gymId: string) => {
    console.log('[deleteGym] Attempting to delete gym completely using Edge Function:', gymId);

    // We call the specialized Edge Function that has Service Role permissions
    // to delete both the Auth users and the gym record.
    const { data, error } = await supabase.functions.invoke('delete-gym-data', {
      body: { gym_id: gymId }
    });

    if (error) {
      console.error('[deleteGym] Edge Function error:', error);
      throw error;
    }

    console.log('[deleteGym] Success:', data);
    return { success: true, data };
  },

  listUsers: async () => {
    // Force a local session check to ensure headers are fresh
    const { data: { session } } = await supabase.auth.getSession();
    const currentUser = session?.user;

    console.log('[listUsers] Identity:', currentUser?.email, 'UserMetadata:', currentUser?.user_metadata, 'AppMetadata:', currentUser?.app_metadata);
    // console.log('[listUsers] Access Token (First 10 chars):', session?.access_token?.substring(0, 10));

    const { data, error } = await supabase.from('profiles').select('*');
    if (error) throw error;
    const users = data.map((p: any) => ({
      user_id: p.id,
      email: p.email || '',
      name: p.full_name || '',
      role: p.role,
      gym_id: p.gym_id,
      active: true,
      created_at: p.created_at
    }));
    return { success: true, users };
  },

  createUser: async (userData: any) => {
    try {
      console.log('[createUser] Calling manage-users edge function:', userData.email);
      const { data, error } = await supabase.functions.invoke('manage-users', {
        body: {
          action: 'create',
          userData: {
            email: userData.email,
            password: userData.password,
            name: userData.name,
            role: userData.role,
            gym_id: userData.gym_id,
            active: userData.active
          }
        }
      });

      if (error) {
        console.error('[createUser] Edge Function error:', error);
        return { success: false, error: error.message || 'Error en la función de administración' };
      }

      if (data?.error) {
        return { success: false, error: data.error };
      }

      console.log('[createUser] Success:', data);
      return { success: true, user_id: data.user_id };
    } catch (error: any) {
      console.error('[createUser] Error:', error);
      return { success: false, error: error.message || 'Error al crear usuario' };
    }
  },

  updateUser: async (userData: any) => {
    console.log('[updateUser] Attempting to update profile:', userData);

    const updatePayload: Record<string, any> = {};
    if (userData.name !== undefined) updatePayload.full_name = userData.name;
    if (userData.role !== undefined) updatePayload.role = userData.role;
    if (userData.gym_id !== undefined) updatePayload.gym_id = userData.gym_id || null; // Empty string -> null for UUID
    if (userData.active !== undefined) updatePayload.active = userData.active;

    // Only include fields that were provided
    if (Object.keys(updatePayload).length === 0) {
      console.warn('[updateUser] No fields to update');
      return { success: false, error: 'No fields to update' };
    }

    console.log('[updateUser] Update payload:', updatePayload, 'for user_id:', userData.user_id);

    const { data, error } = await supabase
      .from('profiles')
      .update(updatePayload)
      .eq('id', userData.user_id)
      .select();

    if (error) {
      console.error('[updateUser] Supabase error:', error);
      throw error;
    }

    console.log('[updateUser] Success, updated rows:', data);
    return { success: true, data };
  },

  deleteUser: async (userId: string) => {
    try {
      console.log('[deleteUser] Calling manage-users edge function:', userId);
      const { data, error } = await supabase.functions.invoke('manage-users', {
        body: {
          action: 'delete',
          userData: { user_id: userId }
        }
      });

      if (error) {
        console.error('[deleteUser] Edge Function error:', error);
        return { success: false, error: error.message || 'Error en la función de administración' };
      }

      if (data?.error) {
        return { success: false, error: data.error };
      }

      console.log('[deleteUser] Success:', data);
      return { success: true };
    } catch (error: any) {
      console.error('[deleteUser] Error:', error);
      throw error;
    }
  },

  resetUserPassword: async (userId: string) => {
    const { data, error } = await supabase.functions.invoke('admin-reset-password', {
      body: { target_user_id: userId }
    });
    if (error) throw error;
    return { success: true, data };
  },

  getSystemSettings: async () => {
    const { data, error } = await supabase
      .from('system_settings')
      .select('*')
      .order('key', { ascending: true });
    if (error) throw error;
    return { success: true, settings: data };
  },

  updateSystemSetting: async (key: string, value: string) => {
    const { error } = await supabase
      .from('system_settings')
      .update({ value, updated_at: new Date().toISOString() })
      .eq('key', key);
    if (error) throw error;
    return { success: true };
  },

  updateSaasTier: async (tierId: string, updates: any) => {
    const { data, error } = await supabase
      .from('saas_tiers')
      .update(updates)
      .eq('id', tierId)
      .select()
      .single();
    if (error) throw error;
    return { success: true, tier: data };
  },

  createSaasTier: async (tierData: any) => {
    const { data, error } = await supabase
      .from('saas_tiers')
      .insert(tierData)
      .select()
      .single();
    if (error) throw error;
    return { success: true, tier: data };
  },

  deleteSaasTier: async (tierId: string) => {
    const { error } = await supabase
      .from('saas_tiers')
      .delete()
      .eq('id', tierId);
    if (error) throw error;
    return { success: true };
  }
};

// --- COMMERCIAL / BILLING API ---
export const billingApi = {
  calculatePrice: async (gymId: string) => {
    const { data, error } = await supabase.rpc('calculate_gym_price', { p_gym_id: gymId });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return {
      success: true,
      data: {
        basePrice: Number(row?.base_price ?? 0),
        finalPrice: Number(row?.final_price ?? 0),
        discountPct: Number(row?.discount_pct ?? 0),
        billingCycle: row?.billing_cycle ?? 'monthly',
        periodLabel: row?.period_label ?? '/mes',
      },
    };
  },

  updateBillingCycle: async (gymId: string, cycle: 'monthly' | 'annual') => {
    const { error } = await supabase.rpc('update_billing_cycle', { p_gym_id: gymId, p_cycle: cycle });
    if (error) throw error;
    return { success: true };
  },

  markSetupPaid: async (gymId: string) => {
    const { error } = await supabase.rpc('mark_setup_paid', { p_gym_id: gymId });
    if (error) throw error;
    return { success: true };
  },

  unmarkSetupPaid: async (gymId: string) => {
    const { error } = await supabase
      .from('gyms')
      .update({ setup_fee_paid: false, setup_completed_at: null })
      .eq('id', gymId);
    if (error) throw error;
    return { success: true };
  },
};

export const whatsappApi = {
  getAddonStatus: async (gymId: string) => {
    const { data, error } = await supabase
      .from('addons')
      .select('*')
      .eq('gym_id', gymId)
      .eq('type', 'whatsapp_ai')
      .limit(1);
    if (error) throw error;
    return { success: true, addon: data?.[0] || null };
  },

  requestAddon: async (gym_id: string) => {
    const { data, error } = await supabase.functions.invoke('request-whatsapp-addon', {
      body: { gym_id }
    });
    if (error) throw error;
    return { success: true, data };
  },

  directActivate: async (gym_id: string) => {
    const { data, error } = await supabase
      .from('addons')
      .insert({
        gym_id,
        type: 'whatsapp_ai',
        status: 'approved',
        config: { enabled: true }
      })
      .select()
      .single();
    if (error) throw error;
    return { success: true, addon: data };
  },

  deactivateAddon: async (addonId: string) => {
    const { data, error } = await supabase
      .from('addons')
      .update({ status: 'deactivated', approved_at: null })
      .eq('id', addonId)
      .select()
      .single();
    if (error) throw error;
    return { success: true, addon: data };
  },

  approveAddon: async (addonId: string, status: 'approved' | 'rejected') => {
    const { data, error } = await supabase.functions.invoke('approve-whatsapp-addon', {
      body: { addon_id: addonId, status }
    });
    if (error) throw error;
    return { success: true, data };
  },

  listPendingAddons: async () => {
    const { data, error } = await supabase
      .from('addons')
      .select('*, gyms(name)')
      .eq('type', 'whatsapp_ai')
      .eq('status', 'pending_approval');
    if (error) throw error;
    return { success: true, addons: data };
  },

  listConversations: async (gymId?: string) => {
    let query = supabase.from('whatsapp_conversations').select('*').order('last_message_at', { ascending: false });
    if (gymId) query = query.eq('gym_id', gymId);
    const { data, error } = await query;
    if (error) throw error;
    return { success: true, conversations: data };
  },

  getMessages: async (conversationId: string) => {
    const { data, error } = await supabase
      .from('whatsapp_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return { success: true, messages: data };
  },

  sendMessage: async (conversationId: string, content: string) => {
    const { data, error } = await supabase.functions.invoke('send-human-whatsapp-message', {
      body: { conversation_id: conversationId, content }
    });
    if (error) throw error;
    return { success: true, message: data };
  },

  toggleAI: async (conversationId: string, paused: boolean) => {
    const { error } = await supabase
      .from('whatsapp_conversations')
      .update({ ai_paused: paused })
      .eq('id', conversationId);
    if (error) throw error;
    return { success: true };
  },

  updateAssistantConfig: async (addonId: string, config: any) => {
    const { error } = await supabase
      .from('addons')
      .update({ config })
      .eq('id', addonId);
    if (error) throw error;
    return { success: true };
  }
};

export const supportApi = {
  createTicket: async (ticket: Partial<SupportTicket>) => {
    const { data, error } = await supabase
      .from('support_tickets')
      .insert([ticket])
      .select()
      .single();
    if (error) throw error;
    return { success: true, ticket: data };
  },

  listTickets: async (gymId?: string) => {
    let query = supabase
      .from('support_tickets')
      .select('*, gyms(name), profiles(full_name)')
      .order('updated_at', { ascending: false });

    if (gymId) query = query.eq('gym_id', gymId);

    const { data, error } = await query;
    if (error) throw error;
    return {
      success: true,
      tickets: data.map((t: any) => ({
        ...t,
        gym_name: t.gyms?.name,
        user_name: t.profiles?.full_name
      })) as SupportTicket[]
    };
  },

  getTicketMessages: async (ticketId: string) => {
    const { data, error } = await supabase
      .from('support_messages')
      .select('*, profiles(full_name)')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return {
      success: true,
      messages: data.map((m: any) => ({
        ...m,
        user_name: m.profiles?.full_name
      })) as SupportMessage[]
    };
  },

  addMessage: async (message: Partial<SupportMessage>) => {
    const { data, error } = await supabase
      .from('support_messages')
      .insert([message])
      .select()
      .single();
    if (error) throw error;

    // Update ticket's updated_at
    await supabase
      .from('support_tickets')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', message.ticket_id);

    return { success: true, message: data };
  },

  updateTicketStatus: async (ticketId: string, status: SupportTicket['status']) => {
    const { data, error } = await supabase
      .from('support_tickets')
      .update({ status })
      .eq('id', ticketId)
      .select()
      .single();
    if (error) throw error;
    return { success: true, ticket: data };
  },

  uploadAttachment: async (file: File, path: string) => {
    const { data, error } = await supabase.storage
      .from('support_attachments')
      .upload(`${path}/${Date.now()}_${file.name}`, file);

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('support_attachments')
      .getPublicUrl(data.path);

    return { success: true, url: publicUrl };
  }
};

// --- EMPLOYEE MANAGEMENT API ---
export const employeeApi = {
  // List employees (users with gym_staff or gym_supervisor role)
  listEmployees: async (gymId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('gym_id', gymId)
      .in('role', ['gym_staff', 'gym_supervisor'])
      .order('full_name', { ascending: true });

    if (error) throw error;
    return { success: true, employees: data || [] };
  },

  // --- SCHEDULES ---
  listSchedules: async (gymId: string, userId?: string) => {
    let query = supabase
      .from('employee_schedules')
      .select('*, profiles(full_name)')
      .eq('gym_id', gymId)
      .order('day_of_week', { ascending: true });

    if (userId) query = query.eq('user_id', userId);

    const { data, error } = await query;
    if (error) throw error;

    return {
      success: true,
      schedules: (data || []).map((s: any) => ({
        ...s,
        user_name: s.profiles?.full_name
      }))
    };
  },

  createSchedule: async (schedule: {
    gym_id: string;
    user_id: string;
    day_of_week: number;
    start_time: string;
    end_time: string;
  }) => {
    const { data, error } = await supabase
      .from('employee_schedules')
      .insert(schedule)
      .select()
      .single();

    if (error) throw error;
    return { success: true, schedule: data };
  },

  updateSchedule: async (scheduleId: string, updates: Partial<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    is_active: boolean;
  }>) => {
    const { data, error } = await supabase
      .from('employee_schedules')
      .update(updates)
      .eq('id', scheduleId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, schedule: data };
  },

  deleteSchedule: async (scheduleId: string) => {
    const { error } = await supabase
      .from('employee_schedules')
      .delete()
      .eq('id', scheduleId);

    if (error) throw error;
    return { success: true };
  },

  // --- ATTENDANCE ---
  listAttendance: async (gymId: string, startDate?: string, endDate?: string) => {
    let query = supabase
      .from('employee_attendance')
      .select('*, profiles(full_name)')
      .eq('gym_id', gymId)
      .order('check_in', { ascending: false });

    if (startDate) query = query.gte('check_in', startDate);
    if (endDate) query = query.lte('check_in', endDate);

    const { data, error } = await query;
    if (error) throw error;

    return {
      success: true,
      attendance: (data || []).map((a: any) => ({
        ...a,
        user_name: a.profiles?.full_name
      }))
    };
  },

  clockIn: async (gymId: string, userId: string, notes?: string) => {
    // Check if there's already an open attendance record
    const { data: existing } = await supabase
      .from('employee_attendance')
      .select('id')
      .eq('gym_id', gymId)
      .eq('user_id', userId)
      .is('check_out', null)
      .single();

    if (existing) {
      return { success: false, error: 'Ya tienes una entrada activa sin salida' };
    }

    const { data, error } = await supabase
      .from('employee_attendance')
      .insert({
        gym_id: gymId,
        user_id: userId,
        check_in: new Date().toISOString(),
        notes
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, attendance: data };
  },

  clockOut: async (attendanceId: string, notes?: string) => {
    const updates: any = { check_out: new Date().toISOString() };
    if (notes) updates.notes = notes;

    const { data, error } = await supabase
      .from('employee_attendance')
      .update(updates)
      .eq('id', attendanceId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, attendance: data };
  },

  getActiveAttendance: async (gymId: string, userId: string) => {
    const { data, error } = await supabase
      .from('employee_attendance')
      .select('*')
      .eq('gym_id', gymId)
      .eq('user_id', userId)
      .is('check_out', null)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return { success: true, attendance: data || null };
  },

  // --- PAYROLL ---
  listPayroll: async (gymId: string, userId?: string) => {
    let query = supabase
      .from('employee_payroll')
      .select('*, profiles(full_name)')
      .eq('gym_id', gymId)
      .order('period_start', { ascending: false });

    if (userId) query = query.eq('user_id', userId);

    const { data, error } = await query;
    if (error) throw error;

    return {
      success: true,
      payroll: (data || []).map((p: any) => ({
        ...p,
        user_name: p.profiles?.full_name
      }))
    };
  },

  createPayroll: async (payroll: {
    gym_id: string;
    user_id: string;
    period_start: string;
    period_end: string;
    base_salary: number;
    bonuses?: number;
    deductions?: number;
    notes?: string;
  }) => {
    const total_paid = payroll.base_salary + (payroll.bonuses || 0) - (payroll.deductions || 0);

    const { data, error } = await supabase
      .from('employee_payroll')
      .insert({
        ...payroll,
        bonuses: payroll.bonuses || 0,
        deductions: payroll.deductions || 0,
        total_paid,
        status: 'pending'
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, payroll: data };
  },

  updatePayroll: async (payrollId: string, updates: Partial<{
    base_salary: number;
    bonuses: number;
    deductions: number;
    status: 'pending' | 'paid';
    payment_date: string;
    notes: string;
  }>) => {
    // Recalculate total if salary components change
    if (updates.base_salary !== undefined || updates.bonuses !== undefined || updates.deductions !== undefined) {
      const { data: current } = await supabase
        .from('employee_payroll')
        .select('base_salary, bonuses, deductions')
        .eq('id', payrollId)
        .single();

      if (current) {
        const base = updates.base_salary ?? current.base_salary;
        const bonuses = updates.bonuses ?? current.bonuses;
        const deductions = updates.deductions ?? current.deductions;
        (updates as any).total_paid = base + bonuses - deductions;
      }
    }

    const { data, error } = await supabase
      .from('employee_payroll')
      .update(updates)
      .eq('id', payrollId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, payroll: data };
  },

  markPayrollAsPaid: async (payrollId: string, gymId: string) => {
    // 1. Get the payroll details including employee name
    const { data: payrollData, error: fetchError } = await supabase
      .from('employee_payroll')
      .select('*, profiles(full_name)')
      .eq('id', payrollId)
      .single();

    if (fetchError) throw fetchError;

    const employeeName = payrollData?.profiles?.full_name || 'Empleado';
    const totalPaid = payrollData?.total_paid || 0;
    const periodStart = payrollData?.period_start || '';
    const periodEnd = payrollData?.period_end || '';

    // 2. Create expense record for general balance (ALWAYS)
    let expenseCreated = false;
    try {
      const { error: expenseError } = await supabase
        .from('expenses')
        .insert({
          gym_id: gymId,
          category: 'salary',
          amount: totalPaid,
          description: `Nómina ${employeeName} (${periodStart} a ${periodEnd})`,
          date: new Date().toISOString().split('T')[0]
        });

      if (!expenseError) {
        expenseCreated = true;
      }
    } catch (e) {
      console.log('Error creating expense record:', e);
    }

    // 3. Try to find active cash box and register movement (if box is open)
    let cashMovementCreated = false;
    try {
      const { data: activeBox } = await supabase
        .from('cash_boxes')
        .select('id')
        .eq('gym_id', gymId)
        .eq('status', 'open')
        .single();

      if (activeBox) {
        // Create cash OUT movement for payroll
        const { error: moveError } = await supabase
          .from('cash_movements')
          .insert({
            cash_box_id: activeBox.id,
            amount: totalPaid,
            type: 'out',
            reason: `Pago nómina: ${employeeName}`
          });

        if (!moveError) {
          // Update expected_balance
          const { data: currentBox } = await supabase
            .from('cash_boxes')
            .select('expected_balance')
            .eq('id', activeBox.id)
            .single();

          if (currentBox) {
            await supabase
              .from('cash_boxes')
              .update({
                expected_balance: Number(currentBox.expected_balance) - totalPaid
              })
              .eq('id', activeBox.id);
          }
          cashMovementCreated = true;
        }
      }
    } catch (e) {
      // No active box, continue without cash movement
      console.log('No active cash box, payroll paid without cash movement');
    }

    // 4. Update payroll status
    const { data, error } = await supabase
      .from('employee_payroll')
      .update({
        status: 'paid',
        payment_date: new Date().toISOString()
      })
      .eq('id', payrollId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, payroll: data, expenseCreated, cashMovementCreated };
  },

  deletePayroll: async (payrollId: string) => {
    const { error } = await supabase
      .from('employee_payroll')
      .delete()
      .eq('id', payrollId);

    if (error) throw error;
    return { success: true };
  },

  // --- SCHEDULE TEMPLATES ---
  listTemplates: async (gymId: string) => {
    const { data, error } = await supabase
      .from('schedule_templates')
      .select('*')
      .eq('gym_id', gymId)
      .order('name', { ascending: true });

    if (error) throw error;
    return { success: true, templates: data || [] };
  },

  createTemplate: async (template: {
    gym_id: string;
    name: string;
    days: Array<{ day: number; start: string; end: string; active: boolean }>;
  }) => {
    const { data, error } = await supabase
      .from('schedule_templates')
      .insert(template)
      .select()
      .single();

    if (error) throw error;
    return { success: true, template: data };
  },

  updateTemplate: async (templateId: string, updates: {
    name?: string;
    days?: Array<{ day: number; start: string; end: string; active: boolean }>;
  }) => {
    const { data, error } = await supabase
      .from('schedule_templates')
      .update(updates)
      .eq('id', templateId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, template: data };
  },

  deleteTemplate: async (templateId: string) => {
    const { error } = await supabase
      .from('schedule_templates')
      .delete()
      .eq('id', templateId);

    if (error) throw error;
    return { success: true };
  },

  applyTemplateToEmployee: async (gymId: string, userId: string, templateId: string) => {
    // First delete existing schedules for this employee
    await supabase
      .from('employee_schedules')
      .delete()
      .eq('gym_id', gymId)
      .eq('user_id', userId);

    // Get template
    const { data: template, error: templateError } = await supabase
      .from('schedule_templates')
      .select('days')
      .eq('id', templateId)
      .single();

    if (templateError) throw templateError;

    // Create schedules for each active day
    const activeDays = (template.days as Array<{ day: number; start: string; end: string; active: boolean }>)
      .filter(d => d.active);

    if (activeDays.length > 0) {
      const schedules = activeDays.map(d => ({
        gym_id: gymId,
        user_id: userId,
        template_id: templateId,
        day_of_week: d.day,
        start_time: d.start,
        end_time: d.end,
        is_active: true
      }));

      const { error: insertError } = await supabase
        .from('employee_schedules')
        .insert(schedules);

      if (insertError) throw insertError;
    }

    return { success: true };
  }
};

// --- NUTRICIÓN (add-on opcional) ---

const mapNutritionMeal = (row: any): NutritionMeal => ({
  id: row.id,
  gym_id: row.gym_id,
  plan_id: row.plan_id,
  meal_type: row.meal_type,
  name: row.name,
  description: row.description,
  calories: row.calories ?? null,
  protein: row.protein != null ? Number(row.protein) : null,
  carbs: row.carbs != null ? Number(row.carbs) : null,
  fat: row.fat != null ? Number(row.fat) : null,
  percent_of_target: row.percent_of_target != null ? Number(row.percent_of_target) : null,
  options: Array.isArray(row.options) ? row.options : [],
  sort_order: row.sort_order ?? 0,
  created_at: row.created_at,
});

const mapNutritionPlan = (row: any): NutritionPlan => ({
  id: row.id,
  gym_id: row.gym_id,
  client_id: row.client_id,
  name: row.name,
  description: row.description,
  objective: row.objective,
  target_calories: row.target_calories ?? null,
  target_protein: row.target_protein != null ? Number(row.target_protein) : null,
  target_carbs: row.target_carbs != null ? Number(row.target_carbs) : null,
  target_fat: row.target_fat != null ? Number(row.target_fat) : null,
  start_date: row.start_date,
  end_date: row.end_date,
  status: row.status || 'active',
  notes: row.notes,
  created_by: row.created_by,
  created_at: row.created_at,
  client_name: row.clients ? cleanName(row.clients.first_name, row.clients.last_name) : undefined,
  meals: Array.isArray(row.nutrition_meals)
    ? row.nutrition_meals.map(mapNutritionMeal).sort((a: NutritionMeal, b: NutritionMeal) => a.sort_order - b.sort_order)
    : undefined,
});

const mapNutritionProgress = (row: any): NutritionProgress => ({
  id: row.id,
  gym_id: row.gym_id,
  client_id: row.client_id,
  plan_id: row.plan_id,
  recorded_at: row.recorded_at,
  weight_kg: row.weight_kg != null ? Number(row.weight_kg) : null,
  height_cm: row.height_cm != null ? Number(row.height_cm) : null,
  sitting_height_cm: row.sitting_height_cm != null ? Number(row.sitting_height_cm) : null,
  sex: row.sex,
  age_years: row.age_years ?? null,
  body_fat_pct: row.body_fat_pct != null ? Number(row.body_fat_pct) : null,
  muscle_kg: row.muscle_kg != null ? Number(row.muscle_kg) : null,
  waist_cm: row.waist_cm != null ? Number(row.waist_cm) : null,
  hip_cm: row.hip_cm != null ? Number(row.hip_cm) : null,
  chest_cm: row.chest_cm != null ? Number(row.chest_cm) : null,
  arm_cm: row.arm_cm != null ? Number(row.arm_cm) : null,
  thigh_cm: row.thigh_cm != null ? Number(row.thigh_cm) : null,
  bmi: row.bmi != null ? Number(row.bmi) : null,
  activity_level: row.activity_level,
  formula: row.formula,
  skinfolds: row.skinfolds || {},
  perimeters: row.perimeters || {},
  diameters: row.diameters || {},
  body_density: row.body_density != null ? Number(row.body_density) : null,
  fat_mass_kg: row.fat_mass_kg != null ? Number(row.fat_mass_kg) : null,
  lean_mass_kg: row.lean_mass_kg != null ? Number(row.lean_mass_kg) : null,
  metabolic_age: row.metabolic_age != null ? Number(row.metabolic_age) : null,
  visceral_fat_kg: row.visceral_fat_kg != null ? Number(row.visceral_fat_kg) : null,
  visceral_fat_index: row.visceral_fat_index != null ? Number(row.visceral_fat_index) : null,
  visceral_fat_source: row.visceral_fat_source ?? null,
  muscle_mass_kg: row.muscle_mass_kg != null ? Number(row.muscle_mass_kg) : null,
  icc: row.icc != null ? Number(row.icc) : null,
  ict: row.ict != null ? Number(row.ict) : null,
  bmr: row.bmr != null ? Number(row.bmr) : null,
  tdee: row.tdee != null ? Number(row.tdee) : null,
  target_calories: row.target_calories != null ? Number(row.target_calories) : null,
  target_protein: row.target_protein != null ? Number(row.target_protein) : null,
  target_carbs: row.target_carbs != null ? Number(row.target_carbs) : null,
  target_fat: row.target_fat != null ? Number(row.target_fat) : null,
  adherence_score: row.adherence_score ?? null,
  notes: row.notes,
  created_by: row.created_by,
  created_at: row.created_at,
  client_name: row.clients ? cleanName(row.clients.first_name, row.clients.last_name) : undefined,
});

const nutritionPlanPayload = (plan: any, gymId?: string) => ({
  gym_id: gymId || plan.gym_id,
  client_id: plan.client_id || null,
  name: plan.name,
  description: plan.description || null,
  objective: plan.objective || null,
  target_calories: plan.target_calories ?? null,
  target_protein: plan.target_protein ?? null,
  target_carbs: plan.target_carbs ?? null,
  target_fat: plan.target_fat ?? null,
  start_date: plan.start_date || null,
  end_date: plan.end_date || null,
  status: plan.status || 'active',
  notes: plan.notes || null,
});

const nutritionProgressPayload = (p: any, gymId?: string) => ({
  gym_id: gymId || p.gym_id,
  client_id: p.client_id,
  plan_id: p.plan_id || null,
  recorded_at: p.recorded_at || new Date().toISOString().slice(0, 10),
  weight_kg: p.weight_kg ?? null,
  height_cm: p.height_cm ?? null,
  sitting_height_cm: p.sitting_height_cm ?? null,
  sex: p.sex ?? null,
  age_years: p.age_years ?? null,
  body_fat_pct: p.body_fat_pct ?? null,
  muscle_kg: p.muscle_kg ?? null,
  waist_cm: p.waist_cm ?? null,
  hip_cm: p.hip_cm ?? null,
  chest_cm: p.chest_cm ?? null,
  arm_cm: p.arm_cm ?? null,
  thigh_cm: p.thigh_cm ?? null,
  activity_level: p.activity_level ?? null,
  formula: p.formula ?? null,
  skinfolds: p.skinfolds ?? {},
  perimeters: p.perimeters ?? {},
  diameters: p.diameters ?? {},
  body_density: p.body_density ?? null,
  fat_mass_kg: p.fat_mass_kg ?? null,
  lean_mass_kg: p.lean_mass_kg ?? null,
  metabolic_age: p.metabolic_age ?? null,
  visceral_fat_kg: p.visceral_fat_kg ?? null,
  visceral_fat_index: p.visceral_fat_index ?? null,
  visceral_fat_source: p.visceral_fat_source ?? null,
  muscle_mass_kg: p.muscle_mass_kg ?? null,
  icc: p.icc ?? null,
  ict: p.ict ?? null,
  bmr: p.bmr ?? null,
  tdee: p.tdee ?? null,
  target_calories: p.target_calories ?? null,
  target_protein: p.target_protein ?? null,
  target_carbs: p.target_carbs ?? null,
  target_fat: p.target_fat ?? null,
  adherence_score: p.adherence_score ?? null,
  notes: p.notes || null,
});

const mapClinicalHistory = (row: any): NutritionClinicalHistory => ({
  id: row.id,
  gym_id: row.gym_id,
  client_id: row.client_id,
  allergies: row.allergies,
  intolerances: row.intolerances,
  pathologies: row.pathologies,
  medications: row.medications,
  supplements: row.supplements,
  activity_level: row.activity_level,
  goal: row.goal,
  observations: row.observations,
  created_at: row.created_at,
  updated_at: row.updated_at,
});

const mapAppointment = (row: any): NutritionAppointment => ({
  id: row.id,
  gym_id: row.gym_id,
  client_id: row.client_id,
  scheduled_at: row.scheduled_at,
  type: row.type,
  status: row.status,
  notes: row.notes,
  duration_minutes: row.duration_minutes ?? 30,
  source: row.source ?? 'staff',
  contact_name: row.contact_name,
  contact_phone: row.contact_phone,
  contact_email: row.contact_email,
  cancel_reason: row.cancel_reason,
  cancelled_by: row.cancelled_by,
  cancelled_at: row.cancelled_at,
  arrival_deadline_at: row.arrival_deadline_at,
  arrived_at: row.arrived_at,
  created_by: row.created_by,
  created_at: row.created_at,
  client_name: row.clients ? cleanName(row.clients.first_name, row.clients.last_name) : undefined,
});

export const nutritionApi = {
  listPlans: async (gymId?: string) => {
    let query = supabase
      .from('nutrition_plans')
      .select('*, clients(first_name, last_name), nutrition_meals(*)');
    if (gymId) query = query.eq('gym_id', gymId);
    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;
    return { success: true, plans: (data || []).map(mapNutritionPlan) };
  },

  createPlan: async (plan: any, gymId?: string) => {
    const { data, error } = await supabase
      .from('nutrition_plans')
      .insert({ ...nutritionPlanPayload(plan, gymId), created_by: plan.created_by || null })
      .select('*, clients(first_name, last_name), nutrition_meals(*)')
      .single();

    if (error) throw error;
    return { success: true, plan: mapNutritionPlan(data) };
  },

  updatePlan: async (planId: string, plan: any, gymId?: string) => {
    let query = supabase
      .from('nutrition_plans')
      .update(nutritionPlanPayload(plan, gymId))
      .eq('id', planId);
    if (gymId) query = query.eq('gym_id', gymId);

    const { data, error } = await query
      .select('*, clients(first_name, last_name), nutrition_meals(*)')
      .single();
    if (error) throw error;
    return { success: true, plan: mapNutritionPlan(data) };
  },

  deletePlan: async (planId: string, gymId?: string) => {
    let query = supabase.from('nutrition_plans').delete().eq('id', planId);
    if (gymId) query = query.eq('gym_id', gymId);

    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  addMeal: async (meal: any, gymId?: string) => {
    const { data, error } = await supabase
      .from('nutrition_meals')
      .insert({
        gym_id: gymId || meal.gym_id,
        plan_id: meal.plan_id,
        meal_type: meal.meal_type,
        name: meal.name,
        description: meal.description || null,
        calories: meal.calories ?? null,
        protein: meal.protein ?? null,
        carbs: meal.carbs ?? null,
        fat: meal.fat ?? null,
        percent_of_target: meal.percent_of_target ?? null,
        options: meal.options ?? [],
        sort_order: meal.sort_order ?? 0,
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, meal: mapNutritionMeal(data) };
  },

  updateMeal: async (mealId: string, meal: any, gymId?: string) => {
    let query = supabase
      .from('nutrition_meals')
      .update({
        meal_type: meal.meal_type,
        name: meal.name,
        description: meal.description || null,
        calories: meal.calories ?? null,
        protein: meal.protein ?? null,
        carbs: meal.carbs ?? null,
        fat: meal.fat ?? null,
        percent_of_target: meal.percent_of_target ?? null,
        options: meal.options ?? [],
        sort_order: meal.sort_order ?? 0,
      })
      .eq('id', mealId);
    if (gymId) query = query.eq('gym_id', gymId);

    const { data, error } = await query.select().single();
    if (error) throw error;
    return { success: true, meal: mapNutritionMeal(data) };
  },

  deleteMeal: async (mealId: string, gymId?: string) => {
    let query = supabase.from('nutrition_meals').delete().eq('id', mealId);
    if (gymId) query = query.eq('gym_id', gymId);

    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  listProgress: async (clientId: string, gymId?: string) => {
    let query = supabase
      .from('nutrition_progress')
      .select('*, clients(first_name, last_name)')
      .eq('client_id', clientId);
    if (gymId) query = query.eq('gym_id', gymId);
    query = query.order('recorded_at', { ascending: true });

    const { data, error } = await query;
    if (error) throw error;
    return { success: true, progress: (data || []).map(mapNutritionProgress) };
  },

  importProgressBatch: async (
    gymId: string,
    rows: Record<string, any>[],
    mode: 'create' | 'upsert' = 'create'
  ) => {
    const { data, error } = await supabase.rpc('import_nutrition_progress_batch_staff', {
      p_gym_id: gymId,
      p_rows: rows as any,
      p_mode: mode,
    });

    if (error) return handleApiError(error);
    return { success: true, ...(data as any) };
  },

  createProgress: async (progress: any, gymId?: string) => {
    const { data, error } = await supabase
      .from('nutrition_progress')
      .insert({ ...nutritionProgressPayload(progress, gymId), created_by: progress.created_by || null })
      .select()
      .single();

    if (error) throw error;
    return { success: true, progress: mapNutritionProgress(data) };
  },

  updateProgress: async (progressId: string, progress: any, gymId?: string) => {
    let query = supabase
      .from('nutrition_progress')
      .update(nutritionProgressPayload(progress, gymId))
      .eq('id', progressId);
    if (gymId) query = query.eq('gym_id', gymId);

    const { data, error } = await query.select().single();
    if (error) throw error;
    return { success: true, progress: mapNutritionProgress(data) };
  },

  deleteProgress: async (progressId: string, gymId?: string) => {
    let query = supabase.from('nutrition_progress').delete().eq('id', progressId);
    if (gymId) query = query.eq('gym_id', gymId);

    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  setClientSex: async (clientId: string, sex: string, gymId?: string) => {
    let query = supabase.from('clients').update({ sex }).eq('id', clientId);
    if (gymId) query = query.eq('gym_id', gymId);

    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  getClinicalHistory: async (clientId: string, gymId?: string) => {
    let query = supabase
      .from('nutrition_clinical_history')
      .select('*')
      .eq('client_id', clientId);
    if (gymId) query = query.eq('gym_id', gymId);

    const { data, error } = await query.maybeSingle();
    if (error) throw error;
    return { success: true, history: (data as NutritionClinicalHistory) || null };
  },

  saveClinicalHistory: async (history: any, gymId?: string) => {
    const { data, error } = await supabase
      .from('nutrition_clinical_history')
      .upsert(
        {
          gym_id: gymId || history.gym_id,
          client_id: history.client_id,
          allergies: history.allergies || null,
          intolerances: history.intolerances || null,
          pathologies: history.pathologies || null,
          medications: history.medications || null,
          supplements: history.supplements || null,
          activity_level: history.activity_level || null,
          goal: history.goal || null,
          observations: history.observations || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'gym_id,client_id' }
      )
      .select()
      .single();
    if (error) throw error;
    return { success: true, history: mapClinicalHistory(data) };
  },

  listAppointments: async (gymId?: string) => {
    let query = supabase
      .from('nutrition_appointments')
      .select('*, clients(first_name, last_name)');
    if (gymId) query = query.eq('gym_id', gymId);
    query = query.order('scheduled_at', { ascending: true });

    const { data, error } = await query;
    if (error) throw error;
    return { success: true, appointments: (data || []).map(mapAppointment) };
  },

  createAppointment: async (appt: any, gymId?: string) => {
    const { data, error } = await supabase
      .from('nutrition_appointments')
      .insert({
        gym_id: gymId || appt.gym_id,
        client_id: appt.client_id,
        scheduled_at: appt.scheduled_at,
        type: appt.type || null,
        status: appt.status || 'scheduled',
        notes: appt.notes || null,
        created_by: appt.created_by || null,
      })
      .select('*, clients(first_name, last_name)')
      .single();
    if (error) throw error;
    return { success: true, appointment: mapAppointment(data) };
  },

  updateAppointment: async (id: string, appt: any, gymId?: string) => {
    const updates: any = {
      scheduled_at: appt.scheduled_at,
      type: appt.type || null,
      status: appt.status,
      notes: appt.notes || null,
    };
    if (appt.client_id) updates.client_id = appt.client_id;

    let query = supabase.from('nutrition_appointments').update(updates).eq('id', id);
    if (gymId) query = query.eq('gym_id', gymId);
    const { data, error } = await query.select('*, clients(first_name, last_name)').single();
    if (error) throw error;
    return { success: true, appointment: mapAppointment(data) };
  },

  deleteAppointment: async (id: string, gymId?: string) => {
    let query = supabase.from('nutrition_appointments').delete().eq('id', id);
    if (gymId) query = query.eq('gym_id', gymId);

    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  // --- Disponibilidad de la agenda (reservas) ---
  listAvailability: async (gymId?: string) => {
    let query = supabase.from('nutrition_availability').select('*');
    if (gymId) query = query.eq('gym_id', gymId);
    query = query.order('day_of_week', { ascending: true });

    const { data, error } = await query;
    if (error) throw error;
    return { success: true, availability: (data || []) as NutritionAvailability[] };
  },

  saveAvailability: async (row: Partial<NutritionAvailability>, gymId?: string) => {
    const payload = {
      gym_id: gymId || row.gym_id,
      day_of_week: row.day_of_week,
      start_time: row.start_time,
      end_time: row.end_time,
      slot_minutes: row.slot_minutes ?? 30,
      is_active: row.is_active ?? true,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('nutrition_availability')
      .upsert(payload, { onConflict: 'gym_id,day_of_week' })
      .select()
      .single();
    if (error) throw error;
    return { success: true, availability: data as NutritionAvailability };
  },

  deleteAvailability: async (id: string, gymId?: string) => {
    let query = supabase.from('nutrition_availability').delete().eq('id', id);
    if (gymId) query = query.eq('gym_id', gymId);

    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  listAvailabilityBlocks: async (gymId?: string) => {
    let query = supabase.from('nutrition_availability_blocks').select('*');
    if (gymId) query = query.eq('gym_id', gymId);
    query = query.order('blocked_at', { ascending: true });

    const { data, error } = await query;
    if (error) throw error;
    return { success: true, blocks: (data || []) as NutritionAvailabilityBlock[] };
  },

  createAvailabilityBlock: async (
    blockedAt: string,
    reason: string | null,
    gymId?: string
  ) => {
    const { data, error } = await supabase
      .from('nutrition_availability_blocks')
      .insert({ gym_id: gymId, blocked_at: blockedAt, reason: reason || null })
      .select()
      .single();
    if (error) throw error;
    return { success: true, block: data as NutritionAvailabilityBlock };
  },

  deleteAvailabilityBlock: async (id: string, gymId?: string) => {
    let query = supabase.from('nutrition_availability_blocks').delete().eq('id', id);
    if (gymId) query = query.eq('gym_id', gymId);

    const { error } = await query;
    if (error) throw error;
    return { success: true };
  },

  /** Métricas de citas de nutrición (hoy, próximas 7 días, mes). */
  getNutritionAppointmentStats: async (gymId: string) => {
    const { data, error } = await supabase.rpc('get_nutrition_appointment_stats', { p_gym_id: gymId });
    if (error) return handleApiError(error);
    return { success: true, ...(data as any) };
  },

  /** Marca que la clienta llegó a la cita (RPC de staff). */
  checkInAppointment: async (id: string) => {
    const { data, error } = await supabase.rpc('check_in_nutrition_appointment_staff', {
      p_appointment_id: id,
    });
    if (error) return handleApiError(error);
    if (data?.success === false) return { success: false, error: data.error };
    return { success: true };
  },

  /** Cancela una cita dejando el motivo (RPC de staff, con auditoría). */
  cancelAppointment: async (id: string, reason: string | null, gymId?: string) => {
    const { data, error } = await supabase.rpc('cancel_nutrition_appointment_staff', {
      p_appointment_id: id,
      p_reason: reason || null,
    });
    if (error) return handleApiError(error);
    if (data?.success === false) return { success: false, error: data.error };
    return { success: true };
  },

  /** Configuración de reservas del gym (leída del RPC para tener los defaults). */
  getBookingConfig: async (gymId: string) => {
    const { data, error } = await supabase.rpc('nutrition_booking_config', {
      p_gym_id: gymId,
    });
    if (error) return handleApiError(error);
    return { success: true, config: (data || null) as NutritionBookingConfig | null };
  },

  // --- Reservas públicas (sin sesión) ---
  getPublicBookingInfo: async (slug: string) => {
    const { data, error } = await supabase.rpc('get_nutrition_booking_info_public', {
      p_slug: slug,
    });
    if (error) return handleApiError(error);
    return { success: true, info: data as any };
  },

  getPublicSlots: async (gymId: string, date: string) => {
    const { data, error } = await supabase.rpc('get_nutrition_slots_public', {
      p_gym_id: gymId,
      p_date: date,
    });
    if (error) return handleApiError(error);
    return { success: true, slots: (data as any)?.slots ?? [], timezone: (data as any)?.timezone };
  },

  schedulePublicAppointment: async (payload: {
    gym_id: string;
    name: string;
    phone: string;
    email?: string | null;
    scheduled_at: string;
    type?: string | null;
    notes?: string | null;
  }) => {
    const { data, error } = await supabase.rpc('schedule_nutrition_appointment_public', {
      p_gym_id: payload.gym_id,
      p_name: payload.name,
      p_phone: payload.phone,
      p_email: payload.email || null,
      p_scheduled_at: payload.scheduled_at,
      p_type: payload.type || null,
      p_notes: payload.notes || null,
    });
    if (error) return handleApiError(error);
    if (data?.success === false) return { success: false, error: data.error };
    return { success: true, result: data as any };
  },
};
